/**
 * landing-story.tsx — /historia
 *
 * Scrollytelling narrativo: rolar a página é rolar o tempo, do anoitecer ao
 * amanhecer — capítulo FINAL, com o CTA principal dentro do céu dourado.
 * Cada capítulo é uma cena full-viewport ("pinned" via position:sticky) que
 * se transforma conforme o usuário rola, na mesma gramática visual do app
 * (noite #0A101F/indigo, estrelas ✦, vela `.candle-mini`, papel creme com
 * carimbo, lua em fases, amanhecer em gradiente).
 *
 * Arquitetura de cor (v2 — céu contínuo):
 *   - `<GlobalSky/>` é um único plano FIXO (position:fixed, z-0) atrás de toda
 *     a página, cuja cor é interpolada por `useScroll()` do DOCUMENTO inteiro
 *     (não por capítulo) via `useTransform` — anoitecer → noite profunda →
 *     madrugada → pré-alvorada → amanhecer. Ele também carrega o campo de
 *     estrelas persistente, glows que acompanham o scroll, pena(s) cruzando
 *     em poucos momentos-chave e pétalas raras caindo — tudo aria-hidden,
 *     determinístico e desligado com prefers-reduced-motion.
 *   - Os capítulos NÃO pintam mais fundo opaco próprio — eles passam pela
 *     janela desse céu único (zero troca brusca entre seções). A única
 *     exceção deliberada é o capítulo final (Amanhecer): ele começa
 *     exatamente na cor da noite (handoff sem costura) e então varre, dentro
 *     do seu próprio pin, o gradiente teatral do nascer do sol — é o clímax
 *     visual da história, com gaivotas e nuvens em loop contínuo.
 *   - `useScroll({ target, offset: ['start start','end end'] })` por
 *     capítulo continua dando o progresso [0..1] LOCAL usado para as cenas
 *     internas (constelação se desenhando, penas convergindo, carrossel de
 *     ferramentas etc).
 *   - `useReducedMotion` → cada capítulo widthdraws o pin e renderiza a cena
 *     já "resolvida" (estado final); loops autoplay (gaivotas, nuvens, pena,
 *     pétalas) somem inteiramente.
 *
 * DO NOT: usar bibliotecas de scroll pesadas (gsap/lenis) — só CSS/SVG/motion.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useMotionValue,
  useMotionTemplate,
  useMotionValueEvent,
  useReducedMotion,
  useInView,
  type MotionValue,
  type Variants,
} from 'motion/react';
import { Download, Menu, X } from 'lucide-react';
import { BornCandle } from '../components/moments';
import { EditorSmoke, type SmokeHandle, type WritingEffect } from '../components/editor-smoke';
import { DefaultAvatar } from '../components/default-avatar';
import { LangSelect } from '../components/lang-select';
import { ThemeSwitch } from '../components/theme-switch';
import { useTheme } from '../hooks/use-theme';
import { useCurrency } from '../lib/pricing';

// ---------------------------------------------------------------------------
// Tokens — paleta aprovada (indigo profundo + periwinkle + amarelo-lua).
// Zero âmbar saturado, zero roxo como acento.
//
// INK/INK_DIM/MOON_TEXT/SHADOW resolve through CSS custom properties scoped
// by `.ls-root.dark` / `.ls-root.light` (see `themeVarsCss` below) so every
// component that already reads these constants automatically follows the
// page's own sun/moon toggle — zero prop-drilling needed for the vast
// majority of the page. Only the handful of spots that need genuinely
// different treatment per theme (GlobalSky's stops, ChapterFive's "always
// white on a saturated dusk" text) take an explicit `theme` prop instead.
// ---------------------------------------------------------------------------
const INK = 'var(--ls-ink)';
const INK_DIM = 'var(--ls-ink-dim)';
const PERIWINKLE = 'var(--color-primary-300)'; // #92A4D6
const MOON = 'var(--ember)'; // #DFC993
const MOON_HOT = 'var(--ember-hot)'; // #F6E9C8
/** Amber used as legible INLINE TEXT straight on the sky (eyebrows, micro-pitch
 * feature line, stat callouts) — pale ember gold in the dark sky, a deep amber
 * ink in the light sky so it clears AA contrast. Decorative fills (orb icon,
 * badges, glows, CTA gradient) keep the literal MOON/MOON_HOT tokens above —
 * those sit on their own opaque/high-contrast surface, not on open sky. */
const MOON_TEXT = 'var(--ls-moon-text)';
const PAPER = 'linear-gradient(180deg,#F6F1E4,#EAE2CE)';
const PAPER_INK = '#241C36';
const PAPER_STAMP = '#8A3B2E';

/** Theme-scoped tokens. Values chosen so INK/INK_DIM/MOON_TEXT keep AA+
 * contrast against both the dark-night and light-day GlobalSky ranges. */
const THEME_VARS_CSS = `
  .ls-root.dark {
    --ls-ink: #EBEEFA;
    --ls-ink-dim: #9AA8D4;
    --ls-moon-text: #DFC993;
    --ls-shadow: #0A101F;
    --ls-nav-rgb: 7, 11, 22;
  }
  .ls-root.light {
    --ls-ink: #1A1F35;
    --ls-ink-dim: #4B5578;
    --ls-moon-text: #7A4E1B;
    --ls-shadow: #EDE7F5;
    --ls-nav-rgb: 221, 228, 245;
  }
`;

const MONO: CSSProperties = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

const APK_URL = 'https://reformu.lat/download/reformulat.apk';

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

// ---------------------------------------------------------------------------
// Alturas de capítulo (vh) — fonte única de verdade para o mapa de cor global
// e para o scaffold de cada capítulo. Ajustar aqui reflete em tudo.
// ---------------------------------------------------------------------------
const CHAPTER_VH = {
  zero: 200,
  one: 250,
  two: 240,
  three: 200,
  tools: 340,
  four: 190,
  pricing: 220,
  five: 280,
} as const;

const VH_TOTAL = Object.values(CHAPTER_VH).reduce((a, b) => a + b, 0);
function cumFrac(keys: (keyof typeof CHAPTER_VH)[]) {
  return keys.reduce((sum, k) => sum + CHAPTER_VH[k], 0) / VH_TOTAL;
}
/** Fração acumulada [0..1] do fim de cada capítulo, no scroll da página inteira. */
const B = {
  zeroEnd: cumFrac(['zero']),
  oneEnd: cumFrac(['zero', 'one']),
  twoEnd: cumFrac(['zero', 'one', 'two']),
  threeEnd: cumFrac(['zero', 'one', 'two', 'three']),
  toolsEnd: cumFrac(['zero', 'one', 'two', 'three', 'tools']),
  fourEnd: cumFrac(['zero', 'one', 'two', 'three', 'tools', 'four']),
  pricingEnd: cumFrac(['zero', 'one', 'two', 'three', 'tools', 'four', 'pricing']),
  fiveEnd: 1,
};

// ---------------------------------------------------------------------------
// Deterministic decorative data (never Math.random)
// ---------------------------------------------------------------------------
/** Campo de estrelas persistente do céu global — fixo à viewport, não ao documento. */
const GLOBAL_STARS = Array.from({ length: 58 }, (_, i) => {
  const left = (i * 29) % 100;
  const top = (i * 17) % 96;
  const size = [1, 1.5, 2.1][i % 3];
  const twinkleDelay = (i % 13) * 0.32;
  return { left, top, size, twinkleDelay, key: `gstar-${i}` };
});

const AMBIENT_PETALS = Array.from({ length: 8 }, (_, i) => {
  const left = 6 + ((i * 127) % 88);
  const duration = 22 + (i % 4) * 6;
  const delay = -((i * 53) % duration);
  const drift = i % 2 === 0 ? 1 : -1;
  const isLeaf = i % 3 === 0;
  return { left, duration, delay, drift, isLeaf, key: `petal-${i}` };
});

const CONSTELLATION_PATH = 'M20,86 L75,52 L130,26 L185,52 L240,86';
const CONSTELLATION_POINTS = [
  { x: 20, y: 86 },
  { x: 75, y: 52 },
  { x: 130, y: 26 },
  { x: 185, y: 52 },
  { x: 240, y: 86 },
];

const CH3_SPARKS = [
  { dx: -46, dy: -30, delay: 0.05 },
  { dx: 44, dy: -36, delay: 0.14 },
  { dx: -32, dy: 34, delay: 0.22 },
  { dx: 40, dy: 30, delay: 0.3 },
];

interface FichaLayout {
  id: 'character' | 'place' | 'faction' | 'object';
  left: number;
  top: number;
  rotate: number;
  hiddenX: number;
  hiddenY: number;
}

// Coordinates below were back-solved from measured (rotated) bounding boxes
// so every card's actual on-screen footprint — not just its unrotated CSS
// box — clears a ~16px margin inside the 660×410 orbit. See ChapterTwo for
// the box size; do not change one without re-validating the other via the
// probe script (measure-ch2.cjs in the scratchpad).
const FICHA_LAYOUT: FichaLayout[] = [
  { id: 'character', left: 35, top: 28, rotate: -6, hiddenX: 190, hiddenY: 118 },
  { id: 'place', left: 431, top: 29, rotate: 5, hiddenX: -190, hiddenY: 130 },
  { id: 'faction', left: 432, top: 280, rotate: 4, hiddenX: 0, hiddenY: -132 },
  { id: 'object', left: 243, top: 281, rotate: -4, hiddenX: -150, hiddenY: -110 },
];

// ---------------------------------------------------------------------------
// Reveal-on-scroll helpers — variants + stagger, no per-item hooks.
// ---------------------------------------------------------------------------
const staggerContainer = (stagger = 0.14, delayChildren = 0): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

const popIn: Variants = {
  hidden: { opacity: 0, scale: 0.4, y: 14 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 20 } },
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

/** Escalated spring "flying in" for cards stacked on mobile — no room for an
 * orbit layout, but the entrance still reads as magic, not a static list. */
function mobileFlyIn(finalRotate: number): Variants {
  return {
    hidden: { opacity: 0, scale: 0.42, y: 54, rotate: 0 },
    visible: { opacity: 1, scale: 1, y: 0, rotate: finalRotate, transition: { type: 'spring', stiffness: 210, damping: 19 } },
  };
}

function revealProps(reduced: boolean) {
  return reduced
    ? ({ initial: 'visible', animate: 'visible' } as const)
    : ({ initial: 'hidden', whileInView: 'visible', viewport: { once: true, amount: 0.4 } } as const);
}

// ---------------------------------------------------------------------------
// ChapterShell — the sticky-pin scaffold shared by every chapter. When
// `reduced` is true it drops the tall wrapper + sticky pin entirely and
// renders a normal, static, already-arrived full-viewport-ish section.
// Chapters no longer paint an opaque background here — the global sky shows
// through; `background` is reserved for local decorative-only content.
// ---------------------------------------------------------------------------
function ChapterShell({
  sectionRef,
  reduced,
  heightVh,
  ariaLabel,
  background,
  children,
  id,
}: {
  sectionRef: RefObject<HTMLDivElement | null>;
  reduced: boolean;
  heightVh: number;
  ariaLabel: string;
  background?: ReactNode;
  children: ReactNode;
  /** Optional anchor id — target of the header's smooth-scroll nav links. */
  id?: string;
}) {
  if (reduced) {
    return (
      <section
        id={id}
        ref={sectionRef}
        aria-label={ariaLabel}
        className="relative flex min-h-dvh w-full scroll-mt-24 items-center justify-center overflow-hidden px-5 py-24 sm:px-8"
      >
        {background}
        <div className="relative z-10 flex w-full flex-col items-center">{children}</div>
      </section>
    );
  }
  return (
    <section id={id} ref={sectionRef} aria-label={ariaLabel} className="relative w-full scroll-mt-24" style={{ height: `${heightVh}vh` }}>
      {/* Vertical breathing room (py) is on top of the horizontal gutter (px) so
          every chapter's content column has real air above/below it and never
          touches the viewport edges, even when the fixed header sits on top. */}
      <div className="sticky top-0 flex h-dvh w-full items-center justify-center overflow-hidden px-6 py-10 sm:px-8 sm:py-14">
        {background}
        <div className="relative z-10 flex w-full flex-col items-center">{children}</div>
      </div>
    </section>
  );
}

/** Progress [0..1] of a chapter's own scroll range; pinned at 1 (arrived) when reduced-motion. */
function useChapterProgress(ref: RefObject<HTMLDivElement | null>, reduced: boolean): MotionValue<number> {
  const { scrollYProgress } = useScroll({ target: ref as RefObject<HTMLElement>, offset: ['start start', 'end end'] });
  const settled = useMotionValue(1);
  return reduced ? settled : scrollYProgress;
}

// ---------------------------------------------------------------------------
// Shared chrome — eyebrow / title / sub / micro-pitch
// ---------------------------------------------------------------------------
function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <p className="text-[10px] uppercase tracking-[0.34em] sm:text-[11px]" style={{ ...MONO, color: MOON_TEXT, ...style }}>
      ✦ {children}
    </p>
  );
}

function Headline({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <h2
      className="max-w-[18ch] text-center font-serif font-medium leading-[1.08]"
      style={{ fontSize: 'clamp(1.9rem, 5.4vw, 3.6rem)', color: INK, ...style }}
    >
      {children}
    </h2>
  );
}

function SubText({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <p className="max-w-[42ch] text-center text-[14px] leading-relaxed sm:text-[16px]" style={{ color: INK_DIM, ...style }}>
      {children}
    </p>
  );
}

/**
 * Fixed-slot feature callout — same alignment/spacing in every chapter that
 * uses it, always as the last element ("rodapé da cena"): a thin gold rule,
 * one short mono line naming the feature, one short serif line selling it.
 * Chapters where a pitch would only add clutter simply don't render one.
 */
function MicroPitch({ feature, benefit, style }: { feature: string; benefit: string; style?: CSSProperties }) {
  return (
    <div className="mt-7 flex w-full flex-col items-center gap-1.5 px-2 text-center" style={style}>
      <span aria-hidden className="h-px w-8" style={{ background: 'linear-gradient(90deg, transparent, rgba(223,201,147,0.55), transparent)' }} />
      <p className="text-[9.5px] font-semibold uppercase tracking-[0.26em]" style={{ ...MONO, color: MOON_TEXT }}>
        {feature}
      </p>
      <p className="max-w-[27ch] font-serif text-[13.5px] font-medium leading-snug" style={{ color: INK }}>
        {benefit}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nav — paridade adaptada da landing principal: logo + âncoras para os
// capítulos-chave + seletor de idioma + Entrar/Criar conta, sobre o céu
// transparente, com um leve blur/escurecida quando a página rola. Mobile
// colapsa para logo + Criar conta + um menu mínimo com o resto.
// ---------------------------------------------------------------------------
const STORY_NAV_LINKS = [
  { id: 'jornada', labelKey: 'landingStory.nav.linkJourney', fallback: 'A jornada' },
  { id: 'ferramentas', labelKey: 'landingStory.nav.linkTools', fallback: 'Ferramentas' },
  { id: 'planos', labelKey: 'landingStory.nav.linkPlans', fallback: 'Planos' },
] as const;

function scrollToStoryChapter(id: string, reduced: boolean) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}

function StoryNav({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scrollY, scrollYProgress } = useScroll();
  // The header must stay legible right at the very top (a light presence),
  // then progressively MELT INTO THE SKY as the reader descends the whole
  // document — by the tools/allies chapters it should already be thinning
  // out, and by the dawn finale it's essentially gone, so the fixed chrome
  // never competes with the cinematic scenes underneath. Two factors are
  // combined: a fast pixel-based fade-IN (so it doesn't pop instantly at
  // y=0) and a slow, whole-document fade-OUT driven by scroll progress.
  const fadeIn = useTransform(scrollY, [0, 90], [0, 1]);
  const fadeOut = useTransform(scrollYProgress, [0, B.fourEnd, 1], [1, 1, 0]);
  // Base scrim — always at least a light presence at the top, dissolving
  // across the whole page (no blur, just a soft gradient behind the logo).
  const baseScrimOpacity = fadeOut;
  // Glassy blurred chrome — only kicks in once the reader actually scrolls,
  // capped at a much softer ~0.5 max (was 1) so it never reads as a solid
  // bar, and dissolves the same way as the base scrim toward the end.
  const chromeOpacity = useTransform([fadeIn, fadeOut], ([a, b]) => (a as number) * (b as number) * 0.5);
  const chromeBlur = useTransform(chromeOpacity, [0, 0.5], [0, 16]);
  const chromeBlurFilter = useMotionTemplate`blur(${chromeBlur}px)`;
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLinkClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileOpen(false);
    scrollToStoryChapter(id, reduced);
  };

  const linkStyle: CSSProperties = { ...MONO, color: INK_DIM };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* Base — subtle scrim so the logo stays legible near the top, over the
          darkest/brightest part of the sky; melts away toward the dawn. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{ opacity: baseScrimOpacity, background: 'linear-gradient(180deg, rgba(var(--ls-nav-rgb),0.55), transparent)' }}
      />
      {/* Scrolled chrome — glassy backdrop that fades in as the reader scrolls
          past the very top, then fades back OUT across the rest of the
          document so it never sits as a solid bar over the cinematic scenes. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          opacity: chromeOpacity,
          background: 'linear-gradient(180deg, rgba(var(--ls-nav-rgb),0.88), rgba(var(--ls-nav-rgb),0.66))',
          backdropFilter: chromeBlurFilter,
          WebkitBackdropFilter: chromeBlurFilter,
          boxShadow: '0 1px 0 rgba(168,184,232,0.14)',
        }}
      />

      <div className="relative flex items-center justify-between gap-3 px-5 py-4 sm:px-8">
        <a href="/historia" className="flex shrink-0 items-center gap-2" aria-label="reformu.lat">
          <img src="/log-black.png" alt="" aria-hidden="true" className="h-6 w-6 object-contain dark:hidden sm:h-7 sm:w-7" />
          <img src="/log-white.png" alt="" aria-hidden="true" className="hidden h-6 w-6 object-contain dark:block sm:h-7 sm:w-7" />
          <span className="font-serif text-[15px] font-semibold tracking-tight sm:text-lg" style={{ color: INK }}>
            reformu<span style={{ color: MOON_HOT }}>.</span>lat
          </span>
        </a>

        {/* Desktop — chapter anchors */}
        <nav aria-label={t('landingStory.nav.ariaLabel', 'Navegação da história')} className="hidden items-center gap-7 md:flex">
          {STORY_NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={handleLinkClick(link.id)}
              className="text-[10.5px] uppercase tracking-[0.24em] transition-colors hover:opacity-100"
              style={{ ...linkStyle, opacity: 0.82 }}
            >
              {t(link.labelKey, link.fallback)}
            </a>
          ))}
        </nav>

        {/* Desktop — theme + language + auth */}
        <div className="hidden items-center gap-3 md:flex">
          <ThemeSwitch sz={22} />
          <LangSelect />
          <span aria-hidden className="h-4 w-px" style={{ background: 'rgba(168,184,232,0.25)' }} />
          <button
            type="button"
            onClick={() => navigate('/app?auth=login')}
            className="rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.22em] transition-colors hover:bg-white/10"
            style={{ ...MONO, color: INK, background: 'rgba(168,184,232,0.1)', boxShadow: '0 0 0 1px rgba(168,184,232,0.28) inset' }}
          >
            {t('landingStory.nav.enter', 'Entrar')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/app?auth=signup')}
            className="rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] transition-transform hover:scale-[1.03]"
            style={{ ...MONO, color: '#141005', background: `linear-gradient(180deg, ${MOON_HOT}, ${MOON})`, boxShadow: '0 4px 16px rgba(223,201,147,0.35)' }}
          >
            {t('landingStory.nav.signup', 'Criar conta')}
          </button>
        </div>

        {/* Mobile — collapsed: logo (above) + Criar conta + menu */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => navigate('/app?auth=signup')}
            className="rounded-full px-3.5 py-2 text-[9px] font-semibold uppercase tracking-[0.18em]"
            style={{ ...MONO, color: '#141005', background: `linear-gradient(180deg, ${MOON_HOT}, ${MOON})` }}
          >
            {t('landingStory.nav.signup', 'Criar conta')}
          </button>
          <button
            type="button"
            aria-label={mobileOpen ? t('landingStory.nav.closeMenu', 'Fechar menu') : t('landingStory.nav.openMenu', 'Abrir menu')}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="grid h-9 w-9 place-items-center rounded-full"
            style={{ color: INK, background: 'rgba(168,184,232,0.1)', boxShadow: '0 0 0 1px rgba(168,184,232,0.28) inset' }}
          >
            {mobileOpen ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </div>

      {/* Mobile menu — minimal panel with chapter anchors + language + Entrar */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={reduced ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="relative mx-4 mb-4 flex flex-col gap-1 rounded-2xl px-4 py-4 md:hidden"
            style={{ background: 'rgba(var(--ls-nav-rgb),0.94)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', boxShadow: '0 24px 60px -20px rgba(0,0,0,0.75), 0 0 0 1px rgba(168,184,232,0.18) inset' }}
          >
            {STORY_NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={handleLinkClick(link.id)}
                className="rounded-lg px-2 py-2.5 text-[11px] uppercase tracking-[0.2em]"
                style={linkStyle}
              >
                {t(link.labelKey, link.fallback)}
              </a>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t pt-3" style={{ borderColor: 'rgba(168,184,232,0.16)' }}>
              <div className="flex items-center gap-2.5">
                <ThemeSwitch sz={20} />
                <LangSelect />
              </div>
              <button
                type="button"
                onClick={() => {
                  navigate('/app?auth=login');
                  setMobileOpen(false);
                }}
                className="rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.2em]"
                style={{ ...MONO, color: INK, background: 'rgba(168,184,232,0.1)', boxShadow: '0 0 0 1px rgba(168,184,232,0.28) inset' }}
              >
                {t('landingStory.nav.enter', 'Entrar')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/** Thin scroll-progress line across the very top of the whole page. */
function StoryProgressBar() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      aria-hidden
      className="fixed inset-x-0 top-0 z-[60] h-[2px] origin-left"
      style={{
        scaleX: scrollYProgress,
        background: `linear-gradient(90deg, ${PERIWINKLE}, ${MOON_HOT})`,
        boxShadow: `0 0 8px rgba(246,233,200,0.5)`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Local keyframes — twinkle / bounce / float / petal-fall / gull-flap.
// Reduced-motion turns them off at the CSS level too (defense in depth, on
// top of the per-chapter `reduced` branching).
// ---------------------------------------------------------------------------
function StoryStyles() {
  return (
    <style>{`
      ${THEME_VARS_CSS}
      @keyframes ls-twinkle { 0%,100% { opacity: .15; } 50% { opacity: 1; } }
      .ls-star { animation: ls-twinkle 3.6s ease-in-out infinite; }
      @keyframes ls-bounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(6px); } }
      .ls-bounce { animation: ls-bounce 1.8s ease-in-out infinite; }
      @keyframes ls-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      .ls-float { animation: ls-float 5s ease-in-out infinite; }
      @keyframes ls-petal-fall {
        0%   { transform: translate(0, -8vh) rotate(0deg); opacity: 0; }
        8%   { opacity: .55; }
        50%  { transform: translate(calc(var(--drift, 1) * 34px), 54vh) rotate(160deg); }
        92%  { opacity: .4; }
        100% { transform: translate(calc(var(--drift, 1) * 68px), 116vh) rotate(320deg); opacity: 0; }
      }
      .ls-petal { animation-name: ls-petal-fall; animation-timing-function: linear; animation-iteration-count: infinite; }
      @keyframes ls-leaf-sway {
        0%,100% { transform: translateX(0) rotate(0deg); }
        50% { transform: translateX(var(--sway, 14px)) rotate(var(--sway-rot, 8deg)); }
      }
      .ls-leaf-sway { animation-name: ls-leaf-sway; animation-duration: var(--sway-dur, 2.2s); animation-timing-function: ease-in-out; animation-iteration-count: infinite; }
      @keyframes ls-sat-blink { 0%, 40% { opacity: .15; } 50% { opacity: 1; } 60%, 100% { opacity: .15; } }
      .ls-sat-blink-red { animation: ls-sat-blink 1.4s steps(1) infinite; }
      .ls-sat-blink-white { animation: ls-sat-blink 1.4s steps(1) infinite; animation-delay: .7s; }
      @media (prefers-reduced-motion: reduce) {
        .ls-star, .ls-bounce, .ls-float, .ls-petal, .ls-leaf-sway, .ls-sat-blink-red, .ls-sat-blink-white { animation: none !important; }
      }
    `}</style>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CÉU GLOBAL — fundo fixo, contínuo, interpolado pelo scroll do documento
// inteiro. Carrega o campo de estrelas, glows que seguem o scroll, penas que
// cruzam a tela em momentos-chave e pétalas raras. Tudo aria-hidden.
// ═══════════════════════════════════════════════════════════════════════════
const SKY_FRACTIONS = [
  0,
  0.045,
  B.zeroEnd,
  B.pricingEnd,
  B.pricingEnd + (1 - B.pricingEnd) * 0.3,
  B.pricingEnd + (1 - B.pricingEnd) * 0.6,
  B.pricingEnd + (1 - B.pricingEnd) * 0.85,
  1,
];
const SKY_TOP = ['#2B3A63', '#12192E', '#070B16', '#070B16', '#070B16', '#070B16', '#070B16', '#070B16'];
const SKY_MID = ['#1B2748', '#0E1730', '#0A101F', '#0A101F', '#131B36', '#33234E', '#5A2F49', '#7A3B4E'];
const SKY_BOTTOM = ['#101A33', '#0A101F', '#0C1326', '#0C1326', '#171F3B', '#5A2F49', '#C4744E', '#E8A15D'];

// Light-theme sky — the SAME narrative arc (starts sereno, ends in the
// "amanhecer"/light climax) reinterpreted as the "writer's day": a soft
// lavender morning settles into a bright, luminous periwinkle/creme daylight
// plateau (the bulk of the journey — same SKY_FRACTIONS shape as the dark
// plateau), then the top/mid/bottom warm together into a soft golden dusk by
// the final chapter. Every stop stays in the approved palette family
// (periwinkle #92A4D6, moon-cream #F6E9C8/#DFC993) — zero saturated amber,
// zero purple, per the brand rule at the top of this file.
const SKY_TOP_LIGHT = ['#DCE3F5', '#CFD9F2', '#C4D0EF', '#C4D0EF', '#C9D2ED', '#D8CFE4', '#E9D4CE', '#F1DEC7'];
const SKY_MID_LIGHT = ['#EDE7F5', '#EFEAF6', '#EEF1FA', '#EEF1FA', '#F2ECEA', '#F5E4D8', '#F8DCC0', '#FBE0BE'];
const SKY_BOTTOM_LIGHT = ['#F5F0E4', '#F6F1E0', '#F6E9C8', '#F6E9C8', '#F5E3C0', '#F6D9A8', '#F5CB8E', '#F3D9B0'];

// ---------------------------------------------------------------------------
// Rajada de folhas — capítulo "planos" → "amanhecer". Uma travessia generosa
// (não um punhado disperso) amarrada a uma janela estreita do scroll do
// documento inteiro, logo depois de "Toda jornada tem seu próprio ritmo" e
// entrando no início do capítulo final. Determinístico, aria-hidden.
//
// Um único vento: TODAS as folhas entram pela direita e atravessam na mesma
// diagonal (direita→esquerda, com leve SUBIDA — o vento do amanhecer as
// carrega para cima, em direção ao sol nascente) — nunca dos dois lados —
// variando apenas velocidade/tamanho/atraso, como uma rajada coerente, não
// um cruzamento de correntes opostas.
// ---------------------------------------------------------------------------
const LEAF_BURST_START = B.pricingEnd - 0.018;
const LEAF_BURST_END = B.pricingEnd + (1 - B.pricingEnd) * 0.4;
const LEAF_BURST_WIDTH = LEAF_BURST_END - LEAF_BURST_START;
const LEAF_BURST_COUNT = 24;

const LEAF_BURST = Array.from({ length: LEAF_BURST_COUNT }, (_, i) => {
  const spanFrac = 0.32 + ((i * 11) % 7) / 7 * 0.24; // 0.32–0.56 of the window width
  const span = LEAF_BURST_WIDTH * spanFrac;
  const startFrac = (i / (LEAF_BURST_COUNT - 1)) * (1 - spanFrac) * 0.92;
  const t0 = LEAF_BURST_START + startFrac * LEAF_BURST_WIDTH;
  const size = 11 + ((i * 5) % 8); // 11–18px — a touch more visible
  const isLeaf = i % 3 !== 0; // majority leaves, some round petals
  // yStart = height when the leaf enters (right edge); yEnd = height when it
  // exits (left edge) and sits ABOVE yStart — a gentle, "leve" lift carried
  // by the dawn wind, not a dramatic climb across the whole viewport.
  const yStart = 46 + ((i * 13) % 34); // 46..80vh — enters at a low/mid height
  const yEnd = yStart - (16 + ((i * 17) % 13)); // rises 16..28vh by the time it exits
  const rotateStart = (i * 41) % 360;
  const rotateSpan = 210 + ((i * 23) % 5) * 68; // 210–482deg
  const swayAmp = 9 + (i % 4) * 6; // 9–27px
  const swayDur = 1.5 + (i % 5) * 0.32; // 1.5–2.78s
  const swayRot = 5 + (i % 3) * 5;
  const tone = i % 2 === 0 ? '246,233,200' : '223,201,147'; // creme / âmbar-lua (rgb triplets)
  const alpha = 0.5 + (i % 4) * 0.07; // 0.5–0.71, a touch more legible
  return { key: `leafburst-${i}`, t0, span, size, isLeaf, yStart, yEnd, rotateStart, rotateSpan, swayAmp, swayDur, swayRot, tone, alpha };
});

function LeafBurstCross({ scrollYProgress, leaf }: { scrollYProgress: MotionValue<number>; leaf: (typeof LEAF_BURST)[number] }) {
  const t0 = leaf.t0;
  const t1 = leaf.t0 + leaf.span;
  const fadeIn = t0 + leaf.span * 0.12;
  const fadeOut = t0 + leaf.span * 0.82;
  const opacity = useTransform(scrollYProgress, [t0, fadeIn, fadeOut, t1], [0, leaf.alpha, leaf.alpha, 0]);
  // Single wind direction — left → right, same diagonal as every other leaf
  // in the burst (the dawn breeze lifting them up toward the sunrise).
  const x = useTransform(scrollYProgress, [t0, t1], ['-12vw', '106vw']);
  const y = useTransform(scrollYProgress, [t0, t1], [`${leaf.yStart}vh`, `${leaf.yEnd}vh`]);
  const rotate = useTransform(scrollYProgress, [t0, t1], [leaf.rotateStart, leaf.rotateStart + leaf.rotateSpan]);
  return (
    <motion.span aria-hidden style={{ opacity, x, y, rotate }} className="pointer-events-none fixed left-0 top-0">
      <span
        className="ls-leaf-sway block"
        style={{
          width: leaf.size,
          height: leaf.isLeaf ? leaf.size * 0.6 : leaf.size * 0.82,
          borderRadius: leaf.isLeaf ? '10px 2px 10px 2px' : '999px',
          background: `rgba(${leaf.tone},${leaf.alpha})`,
          boxShadow: `0 0 ${Math.round(leaf.size * 0.8)}px 0 rgba(246,233,200,0.16)`,
          ['--sway' as string]: `${leaf.swayAmp}px`,
          ['--sway-rot' as string]: `${leaf.swayRot}deg`,
          ['--sway-dur' as string]: `${leaf.swayDur}s`,
        } as CSSProperties}
      />
    </motion.span>
  );
}

// ---------------------------------------------------------------------------
// Cameos raros da noite profunda — astronauta e satélite cruzam JUNTOS
// (levemente defasados) DENTRO do capítulo III · "o céu registra"
// (constelação), simultaneamente em cena, em faixas de altura distintas
// (satélite alto/fundo, astronauta baixo/primeiro plano) e sentidos
// opostos, para não competir com o texto nem entre si. Fixo no timeline do
// scroll do documento inteiro, sempre dentro do trecho de noite constante
// (#0A101F) e com folga das bordas do capítulo (não vaza para "o mundo
// ganha vida" nem para "as ferramentas da jornada").
//
// Astronauta: física espacial — nada de spin por CSS/tempo. A rotação é
// amarrada ao MESMO progress de scroll que dirige a posição (scroll parado
// = giro parado) e o deslocamento é um único trajeto contínuo (x linear +
// y em arco raso monotônico) — no vácuo não se muda de rumo no meio do
// caminho, só se flutua girando sem controle.
// Satélite: mantém a órbita reta em x, mas descreve um arco suave em y
// (parábola/seno raso) em vez de uma diagonal reta.
// ---------------------------------------------------------------------------
const SKY_CROSSING_AT = B.twoEnd + (B.threeEnd - B.twoEnd) * 0.42;
const ASTRO_AT = SKY_CROSSING_AT - 0.006;
const SATELLITE_AT = SKY_CROSSING_AT + 0.006;

/** Builds a piecewise-linear MotionValue approximating a smooth curve: samples
 * `curve(frac)` (frac in [0,1] across [start,end]) at N steps so motion's
 * linear interpolation between keyframes reads as a continuous arc. */
function useArcTransform(scrollYProgress: MotionValue<number>, start: number, end: number, curve: (frac: number) => number, steps = 9) {
  const times = Array.from({ length: steps }, (_, i) => start + (end - start) * (i / (steps - 1)));
  const values = Array.from({ length: steps }, (_, i) => `${curve(i / (steps - 1)).toFixed(1)}vh`);
  return useTransform(scrollYProgress, times, values);
}

function AstronautCrossing({ scrollYProgress, at, reduced }: { scrollYProgress: MotionValue<number>; at: number; reduced: boolean }) {
  const span = 0.07;
  const start = at - span * 0.3;
  const end = at + span * 0.7;
  const fadeIn = start + span * 0.14;
  const fadeOut = end - span * 0.14;
  const opacity = useTransform(scrollYProgress, [start, fadeIn, fadeOut, end], [0, 1, 1, 0]);
  // Single continuous route, no change of heading: x is linear, y is a
  // shallow monotonic arc (always drifting upward on screen, accelerating
  // slightly) — never reverses direction like a bounce would.
  const x = useTransform(scrollYProgress, [start, end], ['-14vw', '112vw']);
  const y = useArcTransform(scrollYProgress, start, end, (f) => 64 - 10 * f - 14 * f * f);
  // Rotation is driven by this window's own scroll progress, not by
  // wall-clock time: stop scrolling and the spin stops with it.
  const rotate = useTransform(scrollYProgress, [start, end], [0, 630]);
  if (reduced) return null;
  return (
    <motion.div aria-hidden style={{ opacity, x, y }} className="pointer-events-none fixed left-0 top-0">
      <motion.img
        src="/story/astronaut.webp"
        alt=""
        style={{
          rotate,
          width: 'clamp(46px, 7vw, 84px)',
          height: 'auto',
          filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.45)) drop-shadow(0 0 10px rgba(146,164,214,0.28))',
        }}
      />
    </motion.div>
  );
}

function SatelliteCrossing({ scrollYProgress, at, reduced }: { scrollYProgress: MotionValue<number>; at: number; reduced: boolean }) {
  const span = 0.065;
  const start = at - span * 0.35;
  const end = at + span * 0.65;
  const fadeIn = start + span * 0.14;
  const fadeOut = end - span * 0.14;
  const opacity = useTransform(scrollYProgress, [start, fadeIn, fadeOut, end], [0, 1, 1, 0]);
  const x = useTransform(scrollYProgress, [start, end], ['108vw', '-16vw']);
  // Shallow arc (parabola/sine) instead of a straight diagonal — dips up
  // slightly through the middle of the crossing, then eases back down.
  const y = useArcTransform(scrollYProgress, start, end, (f) => 14 + 14 * f - 9 * Math.sin(Math.PI * f));
  if (reduced) return null;
  return (
    <motion.div aria-hidden style={{ opacity, x, y, rotate: -16 }} className="pointer-events-none fixed left-0 top-0">
      <div className="relative">
        <img
          src="/story/satellite.webp"
          alt=""
          style={{ width: 'clamp(52px, 8vw, 96px)', height: 'auto', filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.5))' }}
        />
        <span
          className="ls-sat-blink-red absolute rounded-full"
          style={{ right: '18%', top: '30%', width: 3, height: 3, background: '#FF4D4D', boxShadow: '0 0 4px 1px rgba(255,77,77,0.9)' }}
        />
        <span
          className="ls-sat-blink-white absolute rounded-full"
          style={{ right: '30%', top: '46%', width: 2.5, height: 2.5, background: '#F6F1E4', boxShadow: '0 0 4px 1px rgba(246,241,228,0.9)' }}
        />
      </div>
    </motion.div>
  );
}

function GulliconCrossing({ scrollYProgress, at, reduced }: { scrollYProgress: MotionValue<number>; at: number; reduced: boolean }) {
  const opacity = useTransform(scrollYProgress, [at - 0.018, at - 0.006, at + 0.03, at + 0.045], [0, 1, 1, 0]);
  const x = useTransform(scrollYProgress, [at - 0.018, at + 0.045], ['-8vw', '58vw']);
  const y = useTransform(scrollYProgress, [at - 0.018, at + 0.02, at + 0.045], ['62vh', '46vh', '38vh']);
  const rotate = useTransform(scrollYProgress, [at - 0.018, at + 0.045], [8, -14]);
  if (reduced) return null;
  return (
    <motion.span aria-hidden style={{ opacity, x, y, rotate }} className="pointer-events-none fixed left-0 top-0 text-[22px]" >
      🪶
    </motion.span>
  );
}

function GlobalSky({ reduced, theme }: { reduced: boolean; theme: 'light' | 'dark' }) {
  const { scrollYProgress } = useScroll();
  const isLight = theme === 'light';

  const top = useTransform(scrollYProgress, SKY_FRACTIONS, isLight ? SKY_TOP_LIGHT : SKY_TOP);
  const mid = useTransform(scrollYProgress, SKY_FRACTIONS, isLight ? SKY_MID_LIGHT : SKY_MID);
  const bottom = useTransform(scrollYProgress, SKY_FRACTIONS, isLight ? SKY_BOTTOM_LIGHT : SKY_BOTTOM);
  const background = useMotionTemplate`linear-gradient(180deg, ${top} 0%, ${mid} 55%, ${bottom} 100%)`;

  // Persistent starfield — visible through the night span, fades in at dusk
  // and fades out as the sky brightens toward the pre-dawn horizon. A day
  // sky has far fewer visible "stars", so the light theme caps the same
  // curve at a much lower peak instead of full opacity.
  const starOpacity = useTransform(
    scrollYProgress,
    [0, 0.03, B.zeroEnd, B.pricingEnd, B.pricingEnd + (1 - B.pricingEnd) * 0.35],
    isLight ? [0, 0.22, 0.22, 0.22, 0] : [0, 1, 1, 1, 0],
  );

  // Two soft glows that drift slowly down the page as the reader scrolls.
  const glow1Top = useTransform(scrollYProgress, [0, 1], ['-12%', '58%']);
  const glow2Top = useTransform(scrollYProgress, [0, 1], ['38%', '128%']);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.05, B.pricingEnd, 1], [0, 0.5, 0.5, 0.15]);

  const petalsOpacity = useTransform(scrollYProgress, [0.04, B.zeroEnd, B.pricingEnd, B.pricingEnd + (1 - B.pricingEnd) * 0.3], [0, 0.85, 0.85, 0]);

  // Three feather crossings at narratively meaningful transitions — never
  // random, always the same three points in the scroll timeline.
  const featherPoints = [(B.threeEnd + B.toolsEnd) / 2, (B.fourEnd + B.pricingEnd) / 2, B.pricingEnd + (1 - B.pricingEnd) * 0.15];

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <motion.div className="absolute inset-0" style={{ background }} />

      {!reduced && (
        <>
          <motion.div
            className="absolute -left-[10%] h-[46vh] w-[46vh] rounded-full blur-3xl"
            style={{ top: glow1Top, opacity: glowOpacity, background: 'radial-gradient(circle, rgba(146,164,214,0.4), transparent 70%)', mixBlendMode: 'screen' }}
          />
          <motion.div
            className="absolute -right-[14%] h-[52vh] w-[52vh] rounded-full blur-3xl"
            style={{ top: glow2Top, opacity: glowOpacity, background: 'radial-gradient(circle, rgba(223,201,147,0.32), transparent 70%)', mixBlendMode: 'screen' }}
          />
        </>
      )}

      <motion.div className="absolute inset-0" style={{ opacity: starOpacity }}>
        {GLOBAL_STARS.map((s) => (
          <span
            key={s.key}
            className="ls-star absolute rounded-full"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: s.size,
              height: s.size,
              background: MOON_HOT,
              boxShadow: '0 0 6px 1px rgba(246,233,200,0.55)',
              animationDelay: `${s.twinkleDelay}s`,
            }}
          />
        ))}
      </motion.div>

      {!reduced && (
        <motion.div className="absolute inset-0" style={{ opacity: petalsOpacity }}>
          {AMBIENT_PETALS.map((p) => (
            <span
              key={p.key}
              className="ls-petal absolute rounded-full"
              style={{
                left: `${p.left}%`,
                top: '-6vh',
                width: p.isLeaf ? 6 : 4,
                height: p.isLeaf ? 4 : 4,
                borderRadius: p.isLeaf ? '2px 8px 2px 8px' : '999px',
                background: p.isLeaf ? 'rgba(214,170,120,0.55)' : 'rgba(226,150,170,0.5)',
                animationDuration: `${p.duration}s`,
                animationDelay: `${p.delay}s`,
                ['--drift' as string]: p.drift,
              } as CSSProperties}
            />
          ))}
        </motion.div>
      )}

      {!reduced && featherPoints.map((at, i) => <GulliconCrossing key={`feather-${i}`} scrollYProgress={scrollYProgress} at={at} reduced={reduced} />)}

      {!reduced && LEAF_BURST.map((leaf) => <LeafBurstCross key={leaf.key} scrollYProgress={scrollYProgress} leaf={leaf} />)}

      {!reduced && <AstronautCrossing scrollYProgress={scrollYProgress} at={ASTRO_AT} reduced={reduced} />}
      {!reduced && <SatelliteCrossing scrollYProgress={scrollYProgress} at={SATELLITE_AT} reduced={reduced} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO 0 · ANOITECE
// ═══════════════════════════════════════════════════════════════════════════
function ChapterZero({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);

  const moonY = useTransform(progress, [0.1, 0.75], [140, 0]);
  const moonOpacity = useTransform(progress, [0.1, 0.35], [0, 1]);
  const titleOpacity = useTransform(progress, [0, 0.85, 1], [1, 1, reduced ? 1 : 0.35]);
  const titleY = useTransform(progress, [0, 1], [0, -18]);
  const cueOpacity = useTransform(progress, [0, 0.08], [1, 0]);

  return (
    <ChapterShell
      sectionRef={ref}
      reduced={reduced}
      heightVh={CHAPTER_VH.zero}
      ariaLabel={t('landingStory.aria.chapter0', 'Capítulo zero — anoitece')}
      background={
        <>
          <motion.div
            aria-hidden
            style={{ y: moonY, opacity: moonOpacity }}
            className="absolute bottom-[18%] left-1/2 h-[92px] w-[92px] -translate-x-1/2 rounded-full sm:h-[120px] sm:w-[120px]"
          >
            <div
              className="relative h-full w-full overflow-hidden rounded-full"
              style={{ background: 'radial-gradient(circle at 38% 34%,#F6E9C8,#D8C48F 62%,#8A784E 96%)', boxShadow: '0 0 60px rgba(246,233,200,0.45)' }}
            >
              <span className="absolute inset-[-2%] rounded-full" style={{ background: 'var(--ls-shadow)', transform: 'translateX(30%)' }} />
            </div>
          </motion.div>
          <motion.div style={{ opacity: cueOpacity }} className="pointer-events-none absolute inset-x-0 bottom-[8%] flex flex-col items-center gap-1.5">
            <span className="ls-bounce text-[16px]" style={{ color: PERIWINKLE }} aria-hidden>
              ⌄
            </span>
            <span className="text-[9px] uppercase tracking-[0.3em]" style={{ ...MONO, color: INK_DIM }}>
              {t('landingStory.chapter0.scrollCue', 'role para começar')}
            </span>
          </motion.div>
        </>
      }
    >
      <motion.div style={{ opacity: titleOpacity, y: titleY }} className="flex max-w-[30rem] flex-col items-center gap-6 px-3">
        <Eyebrow>{t('landingStory.chapter0.eyebrow', 'capítulo 0 · anoitece')}</Eyebrow>
        <Headline>{t('landingStory.chapter0.title', 'Toda grande história começa numa noite em branco.')}</Headline>
        <SubText>{t('landingStory.chapter0.sub', 'Esta é a jornada de quem decide escrever a sua.')}</SubText>
      </motion.div>
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO I · A PRIMEIRA PALAVRA (+ demonstração dos efeitos de escrita)
// ═══════════════════════════════════════════════════════════════════════════
/** The 6 richest effects in EditorSmoke, each paired with a short prose line typed live so the particles visibly spawn from the words themselves. */
const EFFECT_CYCLE: {
  type: WritingEffect;
  labelKey: string;
  labelFallback: string;
  sentenceKey: string;
  sentenceFallback: string;
}[] = [
  {
    type: 'petals',
    labelKey: 'textSettings.effectPetals',
    labelFallback: 'Pétalas',
    sentenceKey: 'landingStory.chapter1.sentencePetals',
    sentenceFallback: 'As pétalas caem sobre cada palavra nova.',
  },
  {
    type: 'stardust',
    labelKey: 'textSettings.effectStardust',
    labelFallback: 'Poeira Estelar',
    sentenceKey: 'landingStory.chapter1.sentenceStardust',
    sentenceFallback: 'Poeira de estrelas se acende a cada tecla.',
  },
  {
    type: 'mist',
    labelKey: 'textSettings.effectMist',
    labelFallback: 'Névoa Cósmica',
    sentenceKey: 'landingStory.chapter1.sentenceMist',
    sentenceFallback: 'A névoa sobe devagar da tinta fresca.',
  },
  {
    type: 'fireflies',
    labelKey: 'textSettings.effectFireflies',
    labelFallback: 'Vagalumes',
    sentenceKey: 'landingStory.chapter1.sentenceFireflies',
    sentenceFallback: 'Vagalumes seguem sua caneta na escuridão.',
  },
  {
    type: 'inkrain',
    labelKey: 'textSettings.effectInkRain',
    labelFallback: 'Chuva de Tinta',
    sentenceKey: 'landingStory.chapter1.sentenceInkrain',
    sentenceFallback: 'Chuva de tinta escorre pela página.',
  },
  {
    type: 'fire',
    labelKey: 'textSettings.effectFire',
    labelFallback: 'Lareira em Chamas',
    sentenceKey: 'landingStory.chapter1.sentenceFire',
    sentenceFallback: 'Brasas dançam ao redor da sua história.',
  },
];

/**
 * Live demo: a short line of prose is typed out (real typewriter, blinking
 * caret) while the active EditorSmoke effect spawns particles directly from
 * the caret's position — so the magic is visibly born from the text, not
 * from a decorative placeholder bar. Cycles through the 6 best effects.
 */
function EditorEffectsDemo({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);
  const smokeRef = useRef<SmokeHandle>(null);
  const inView = useInView(wrapRef, { amount: 0.4 });
  const [idx, setIdx] = useState(0);
  const active = EFFECT_CYCLE[idx];
  const sentence = t(active.sentenceKey, active.sentenceFallback);
  const [typed, setTyped] = useState(() => (reduced ? sentence.length : 0));

  // Typewriter: types the active sentence, dwells so the effect is legible,
  // then advances to the next effect + sentence and restarts.
  useEffect(() => {
    if (reduced || !inView) return;
    if (typed >= sentence.length) {
      const id = window.setTimeout(() => {
        setIdx((n) => (n + 1) % EFFECT_CYCLE.length);
        setTyped(0);
      }, 1500);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => setTyped((n) => n + 1), 34);
    return () => window.clearTimeout(id);
  }, [typed, sentence, reduced, inView]);

  // Every keystroke of the fake typing spawns particles right at the caret —
  // the effect is literally born from the line being written.
  useEffect(() => {
    if (reduced || !inView || typed === 0) return;
    const el = cursorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    smokeRef.current?.emit(rect.left, rect.top + rect.height / 2);
  }, [typed, reduced, inView]);

  return (
    <div ref={wrapRef} className="relative mt-11 flex w-[min(440px,90vw)] flex-col items-center">
      <p className="mb-3 text-center text-[9px] uppercase tracking-[0.3em]" style={{ ...MONO, color: INK_DIM }}>
        {t('landingStory.chapter1.effectsEyebrow', 'efeitos de escrita ao vivo')}
      </p>
      <div
        className="relative flex min-h-[112px] w-full items-center overflow-hidden rounded-[10px] px-6 py-5 sm:min-h-[124px] sm:px-7 sm:py-6"
        style={{ background: 'rgba(168,184,232,0.05)', boxShadow: '0 0 0 1px rgba(168,184,232,0.18) inset' }}
      >
        {!reduced && <EditorSmoke ref={smokeRef} type={active.type} />}
        <p className="relative z-10 font-mono text-[12.5px] leading-relaxed sm:text-[13.5px]" style={{ color: INK }}>
          {sentence.slice(0, typed)}
          <span
            ref={cursorRef}
            aria-hidden
            className="ml-[1px] inline-block h-[1em] w-[2px] animate-pulse align-middle"
            style={{ background: MOON_HOT }}
          />
        </p>
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={active.type}
          initial={reduced ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? undefined : { opacity: 0, y: -4 }}
          transition={{ duration: 0.35 }}
          className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.28em]"
          style={{ color: MOON_TEXT }}
        >
          {t(active.labelKey, active.labelFallback)}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

function ChapterOne({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);

  const pageRotate = useTransform(progress, [0, 1], [-3, 1.5]);
  const pageY = useTransform(progress, [0, 0.5, 1], [36, 0, -26]);

  const sentence = t('landingStory.chapter1.sentence', 'A noite ainda estava em branco quando ela escreveu a primeira palavra.');
  const pageInView = useInView(pageRef, { once: true, amount: 0.55 });

  const [typed, setTyped] = useState(reduced ? sentence.length : 0);
  const [lit, setLit] = useState(reduced);

  useEffect(() => {
    if (reduced) return;
    if (!pageInView) return;
    if (typed >= sentence.length) {
      const id = window.setTimeout(() => setLit(true), 380);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => setTyped((n) => n + 1), 32);
    return () => window.clearTimeout(id);
  }, [pageInView, typed, reduced, sentence.length]);

  return (
    <ChapterShell
      sectionRef={ref}
      reduced={reduced}
      heightVh={CHAPTER_VH.one}
      ariaLabel={t('landingStory.aria.chapter1', 'Capítulo um — a primeira palavra')}
      id="jornada"
    >
      <div className="mb-7">
        <Eyebrow>{t('landingStory.chapter1.eyebrow', 'capítulo I · a primeira palavra')}</Eyebrow>
      </div>

      <div ref={pageRef} className="relative">
        <motion.div
          style={{ rotate: pageRotate, y: pageY, background: PAPER, boxShadow: '0 40px 90px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(70,55,30,0.25)' }}
          className="relative w-[min(440px,84vw)] rounded-[4px_10px_10px_4px] px-8 py-10 sm:px-10 sm:py-12"
        >
          <p className="font-mono text-[7.5px] uppercase tracking-[0.28em]" style={{ ...MONO, color: '#A8935F' }}>
            {t('landingStory.chapter1.eyebrow', 'capítulo I · a primeira palavra')}
          </p>
          <p className="mt-3 min-h-[4.6em] font-serif text-[18px] leading-relaxed sm:text-[23px]" style={{ color: PAPER_INK }}>
            {sentence.slice(0, typed)}
            {!reduced && typed < sentence.length && (
              <span aria-hidden className="ml-[1px] inline-block h-[1em] w-[2px] animate-pulse align-middle" style={{ background: PAPER_INK }} />
            )}
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={false}
        animate={{ opacity: lit ? 1 : 0, y: lit ? 0 : 10 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="mt-9 flex flex-col items-center gap-4"
      >
        <span className="relative inline-block" style={{ transform: 'scale(1.15)' }}>
          <BornCandle lit={lit} delay={0.05} />
        </span>
        <SubText>{t('landingStory.chapter1.caption', '500 palavras. Uma vela. Todo dia.')}</SubText>
      </motion.div>

      <EditorEffectsDemo reduced={reduced} />
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO II · O MUNDO GANHA VIDA
// ═══════════════════════════════════════════════════════════════════════════
function FichaCard({
  tag,
  name,
  desc,
  stamp,
  style,
  className,
  variants,
  icon,
}: {
  tag: string;
  name: string;
  desc: string;
  stamp: string;
  style?: CSSProperties;
  className?: string;
  variants?: Variants;
  icon?: ReactNode;
}) {
  return (
    <motion.div
      variants={variants}
      style={{ background: PAPER, boxShadow: '0 30px 70px -18px rgba(0,0,0,0.75), 0 0 0 1px rgba(70,55,30,0.25)', ...style }}
      className={`relative rounded-[3px_8px_8px_3px] px-4 py-4 ${className ?? ''}`}
    >
      <div className="flex items-center gap-2">
        {icon}
        <p className="text-[7.5px] uppercase tracking-[0.26em]" style={{ ...MONO, color: '#A8935F' }}>
          {tag}
        </p>
      </div>
      <p className="mt-1.5 font-serif text-[14px] font-medium leading-tight sm:text-[16px]" style={{ color: PAPER_INK }}>
        {name}
      </p>
      <p className="mt-1 text-[10px] leading-snug sm:text-[11px]" style={{ color: '#5A4E3C' }}>
        {desc}
      </p>
      <span
        aria-hidden
        className="absolute right-2 top-2 rounded-[3px] border-[1.5px] px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-[0.14em]"
        style={{ ...MONO, color: PAPER_STAMP, borderColor: 'rgba(138,59,46,0.7)', background: 'rgba(246,241,228,0.6)', transform: 'rotate(-8deg)' }}
      >
        {stamp}
      </span>
    </motion.div>
  );
}

/** Personagem — dossiê com foto real em moldura de polaroide (fallback: avatar gradiente+inicial). */
function PolaroidCard({ name, desc, stamp, alt, style, className, variants }: { name: string; desc: string; stamp: string; alt: string; style?: CSSProperties; className?: string; variants?: Variants }) {
  const [broken, setBroken] = useState(false);
  return (
    <motion.div
      variants={variants}
      style={{ background: PAPER, boxShadow: '0 30px 70px -18px rgba(0,0,0,0.75), 0 0 0 1px rgba(70,55,30,0.25)', ...style }}
      className={`relative rounded-[3px_8px_8px_3px] px-3 pb-4 pt-3 ${className ?? ''}`}
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[2px]" style={{ boxShadow: '0 0 0 1px rgba(70,55,30,0.25)' }}>
        {/* retrato do nosso próprio sistema de avatar (zero dependência externa) */}
        <DefaultAvatar name={name} className="rounded-none" />
      </div>
      <p className="mt-2 text-[7.5px] uppercase tracking-[0.26em]" style={{ ...MONO, color: '#A8935F' }}>
        {stamp}
      </p>
      <p className="mt-1 font-serif text-[14px] font-medium leading-tight sm:text-[16px]" style={{ color: PAPER_INK }}>
        {name}
      </p>
      <p className="mt-1 text-[10px] leading-snug sm:text-[11px]" style={{ color: '#5A4E3C' }}>
        {desc}
      </p>
    </motion.div>
  );
}

function ChapterTwo({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);

  const orbitY = useTransform(progress, [0, 1], [10, -10]);
  const anchorScale = useTransform(progress, [0, 0.3], [0.7, 1]);
  const anchorOpacity = useTransform(progress, [0, 0.2], [0, 1]);

  const iconStyle: CSSProperties = { width: 14, height: 14, borderRadius: 4, overflow: 'hidden', flexShrink: 0 };

  return (
    <ChapterShell
      sectionRef={ref}
      reduced={reduced}
      heightVh={CHAPTER_VH.two}
      ariaLabel={t('landingStory.aria.chapter2', 'Capítulo dois — o mundo ganha vida')}
    >
      <div className="mb-4 flex flex-col items-center gap-3">
        <Eyebrow>{t('landingStory.chapter2.eyebrow', 'capítulo II · o mundo ganha vida')}</Eyebrow>
      </div>

      {/* the world — anchor point + orbiting dossiers */}
      <motion.div
        style={{ y: orbitY }}
        variants={staggerContainer(0.16, 0.05)}
        {...revealProps(reduced)}
        className="relative my-6 hidden h-[410px] w-[660px] shrink-0 lg:block"
      >
        <motion.div
          aria-hidden
          style={{ scale: anchorScale, opacity: anchorOpacity }}
          className="orb-pulse absolute left-1/2 top-1/2 grid h-[54px] w-[54px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full text-[20px]"
        >
          <span
            className="grid h-full w-full place-items-center rounded-full"
            style={{ background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 60%, #9A8756)', color: '#141005', boxShadow: '0 0 30px rgba(223,201,147,0.5)' }}
          >
            ✦
          </span>
        </motion.div>

        <PolaroidCard
          name={t('landingStory.chapter2.card1Name', 'Elenya Duskwright')}
          desc={t('landingStory.chapter2.card1Desc', 'guarda um segredo que ainda não contou nem a si mesma')}
          stamp={t('landingStory.chapter2.stamp1', 'protagonista')}
          alt={t('landingStory.chapter2.charAlt', 'Retrato de Elenya Duskwright')}
          variants={{
            hidden: { opacity: 0, scale: 0.35, x: FICHA_LAYOUT[0].hiddenX, y: FICHA_LAYOUT[0].hiddenY, rotate: 0 },
            visible: { opacity: 1, scale: 1, x: 0, y: 0, rotate: FICHA_LAYOUT[0].rotate, transition: { type: 'spring', stiffness: 160, damping: 17 } },
          }}
          style={{ position: 'absolute', left: FICHA_LAYOUT[0].left, top: FICHA_LAYOUT[0].top, width: 176 }}
        />
        <FichaCard
          tag={t('landingStory.chapter2.tagPlace', 'lugar')}
          name={t('landingStory.chapter2.card2Name', 'Vale de Cinábrio')}
          desc={t('landingStory.chapter2.card2Desc', 'onde a história muda de direção pela primeira vez')}
          stamp={t('landingStory.chapter2.stamp2', 'cenário')}
          icon={<span style={iconStyle}><DefaultAvatar name="Vale de Cinábrio" /></span>}
          variants={{
            hidden: { opacity: 0, scale: 0.35, x: FICHA_LAYOUT[1].hiddenX, y: FICHA_LAYOUT[1].hiddenY, rotate: 0 },
            visible: { opacity: 1, scale: 1, x: 0, y: 0, rotate: FICHA_LAYOUT[1].rotate, transition: { type: 'spring', stiffness: 160, damping: 17, delay: 0.08 } },
          }}
          style={{ position: 'absolute', left: FICHA_LAYOUT[1].left, top: FICHA_LAYOUT[1].top, width: 208 }}
        />
        <FichaCard
          tag={t('landingStory.chapter2.tagFaction', 'facção')}
          name={t('landingStory.chapter2.card3Name', 'A Ordem do Véu')}
          desc={t('landingStory.chapter2.card3Desc', 'ninguém sabe ao certo do que eles têm medo')}
          stamp={t('landingStory.chapter2.stamp3', 'conflito')}
          icon={<span style={iconStyle}><DefaultAvatar name="A Ordem do Véu" /></span>}
          variants={{
            hidden: { opacity: 0, scale: 0.35, x: FICHA_LAYOUT[2].hiddenX, y: FICHA_LAYOUT[2].hiddenY, rotate: 0 },
            visible: { opacity: 1, scale: 1, x: 0, y: 0, rotate: FICHA_LAYOUT[2].rotate, transition: { type: 'spring', stiffness: 160, damping: 17, delay: 0.16 } },
          }}
          style={{ position: 'absolute', left: FICHA_LAYOUT[2].left, top: FICHA_LAYOUT[2].top, width: 208 }}
        />
        <FichaCard
          tag={t('landingStory.chapter2.tagObject', 'objeto')}
          name={t('landingStory.chapter2.card4Name', 'A Chave de Cinábrio')}
          desc={t('landingStory.chapter2.card4Desc', 'abre uma porta que talvez fosse melhor ficar fechada')}
          stamp={t('landingStory.chapter2.stamp4', 'artefato')}
          icon={<span style={iconStyle}><DefaultAvatar name="A Chave de Cinábrio" /></span>}
          variants={{
            hidden: { opacity: 0, scale: 0.35, x: FICHA_LAYOUT[3].hiddenX, y: FICHA_LAYOUT[3].hiddenY, rotate: 0 },
            visible: { opacity: 1, scale: 1, x: 0, y: 0, rotate: FICHA_LAYOUT[3].rotate, transition: { type: 'spring', stiffness: 160, damping: 17, delay: 0.24 } },
          }}
          style={{ position: 'absolute', left: FICHA_LAYOUT[3].left, top: FICHA_LAYOUT[3].top, width: 190 }}
        />
      </motion.div>

      {/* mobile/tablet — no room for the orbit layout, but the cards still pop
          in with the same escalated spring "flying in" language (scale up
          from a distant, tilted, slightly-offset origin). A 2-column grid
          (rather than a single tall stack) keeps the whole dossier — plus the
          headline and pitch below it — comfortably inside one viewport, with
          real air between the cards instead of a cramped column. */}
      <motion.div variants={staggerContainer(0.16, 0.05)} {...revealProps(reduced)} className="grid w-full max-w-[380px] grid-cols-2 gap-4 lg:hidden">
        <PolaroidCard
          name={t('landingStory.chapter2.card1Name', 'Elenya Duskwright')}
          desc={t('landingStory.chapter2.card1Desc', 'guarda um segredo que ainda não contou nem a si mesma')}
          stamp={t('landingStory.chapter2.stamp1', 'protagonista')}
          alt={t('landingStory.chapter2.charAlt', 'Retrato de Elenya Duskwright')}
          variants={mobileFlyIn(FICHA_LAYOUT[0].rotate)}
        />
        <FichaCard
          tag={t('landingStory.chapter2.tagPlace', 'lugar')}
          name={t('landingStory.chapter2.card2Name', 'Vale de Cinábrio')}
          desc={t('landingStory.chapter2.card2Desc', 'onde a história muda de direção pela primeira vez')}
          stamp={t('landingStory.chapter2.stamp2', 'cenário')}
          icon={<span style={iconStyle}><DefaultAvatar name="Vale de Cinábrio" /></span>}
          variants={mobileFlyIn(FICHA_LAYOUT[1].rotate)}
        />
        <FichaCard
          tag={t('landingStory.chapter2.tagFaction', 'facção')}
          name={t('landingStory.chapter2.card3Name', 'A Ordem do Véu')}
          desc={t('landingStory.chapter2.card3Desc', 'ninguém sabe ao certo do que eles têm medo')}
          stamp={t('landingStory.chapter2.stamp3', 'conflito')}
          icon={<span style={iconStyle}><DefaultAvatar name="A Ordem do Véu" /></span>}
          variants={mobileFlyIn(FICHA_LAYOUT[2].rotate)}
        />
        <FichaCard
          tag={t('landingStory.chapter2.tagObject', 'objeto')}
          name={t('landingStory.chapter2.card4Name', 'A Chave de Cinábrio')}
          desc={t('landingStory.chapter2.card4Desc', 'abre uma porta que talvez fosse melhor ficar fechada')}
          stamp={t('landingStory.chapter2.stamp4', 'artefato')}
          icon={<span style={iconStyle}><DefaultAvatar name="A Chave de Cinábrio" /></span>}
          variants={mobileFlyIn(FICHA_LAYOUT[3].rotate)}
        />
      </motion.div>

      <div className="mt-7 flex flex-col items-center gap-3 px-3">
        <Headline style={{ fontSize: 'clamp(1.5rem, 4.2vw, 2.4rem)' }}>{t('landingStory.chapter2.title', 'Cada nome que você escreve vira alguém.')}</Headline>
        <SubText>{t('landingStory.chapter2.archiveCopy', 'Personagens, lugares, facções, objetos e lore — tudo o que você cria fica arquivado, com carimbo, sempre pronto para voltar.')}</SubText>
      </div>

      <MicroPitch
        feature={t('landingStory.chapter2.pitchFeature', 'universo & story bible')}
        benefit={t('landingStory.chapter2.pitchBenefit', 'cada personagem, lugar e segredo — arquivado e pronto pra voltar.')}
      />
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO III · O CÉU REGISTRA
// ═══════════════════════════════════════════════════════════════════════════
function ChapterThree({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);

  const pathLength = useTransform(progress, [0.05, 0.6], [0, 1]);

  return (
    <ChapterShell
      sectionRef={ref}
      reduced={reduced}
      heightVh={CHAPTER_VH.three}
      ariaLabel={t('landingStory.aria.chapter3', 'Capítulo três — o céu registra')}
    >
      <div className="mb-4">
        <Eyebrow>{t('landingStory.chapter3.eyebrow', 'capítulo III · o céu registra')}</Eyebrow>
      </div>

      <motion.div variants={staggerContainer(0.13, 0.1)} {...revealProps(reduced)} className="relative flex flex-col items-center">
        <svg viewBox="0 0 260 118" className="h-[130px] w-[280px] sm:h-[150px] sm:w-[320px]" aria-hidden>
          <motion.path d={CONSTELLATION_PATH} fill="none" stroke="rgba(223,201,147,0.5)" strokeWidth={1.4} style={reduced ? undefined : { pathLength }} />
          {CONSTELLATION_POINTS.map((p, i) => (
            <motion.circle key={`c3-star-${i}`} variants={popIn} cx={p.x} cy={p.y} r={3.4} fill={MOON_HOT} style={{ filter: 'drop-shadow(0 0 4px rgba(246,233,200,0.85))' }} />
          ))}
        </svg>

        <motion.div variants={popIn} className="relative mt-1 flex flex-col items-center">
          {CH3_SPARKS.map((s, i) => (
            <motion.span
              key={`spark-${i}`}
              aria-hidden
              initial={{ opacity: 0, x: 0, y: 0, scale: 0.4 }}
              animate={reduced ? { opacity: 0 } : { opacity: [0, 1, 0], x: [0, s.dx], y: [0, s.dy], scale: [0.4, 1, 0.6] }}
              transition={{ delay: 0.5 + s.delay, duration: 1.1, ease: 'easeOut' }}
              className="pointer-events-none absolute left-1/2 top-1/2 text-[11px]"
              style={{ color: MOON_HOT }}
            >
              ✦
            </motion.span>
          ))}
          <span
            className="rounded-full px-4 py-1.5 font-serif text-[15px] font-medium sm:text-[17px]"
            style={{ color: MOON_TEXT, background: 'rgba(223,201,147,0.08)', boxShadow: '0 0 0 1.5px rgba(223,201,147,0.4) inset' }}
          >
            {t('landingStory.chapter3.levelLabel', 'Nv. 12 · Cronista')}
          </span>
        </motion.div>
      </motion.div>

      <div className="mt-8 flex flex-col items-center gap-3 px-2">
        <Headline style={{ fontSize: 'clamp(1.6rem, 4.6vw, 2.7rem)' }}>{t('landingStory.chapter3.title', 'Cada noite escrita vira uma estrela.')}</Headline>
        <SubText>
          {t('landingStory.chapter3.sub', 'Quase 90 pequenas cerimônias marcam o seu ritual — da brasa da primeira palavra à noite em que o céu inteiro se acende por você.')}
        </SubText>
      </div>

      <MicroPitch
        feature={t('landingStory.chapter3.pitchFeature', 'progresso & conquistas')}
        benefit={t('landingStory.chapter3.pitchBenefit', 'metas, sequências e níveis — escrever vira um hábito que gruda.')}
      />
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO · AS FERRAMENTAS DA JORNADA (6 estágios scrubbed: ghostwriter,
// estrutura, universo/story bible, colaboração ao vivo, exportação multi-
// formato, capas) — entre o céu e os aliados.
// ═══════════════════════════════════════════════════════════════════════════
const TOOLS_STAGE_COUNT = 6;

/** Small circular "seal" badge used by the export stage — DOCX/EPUB/PDF made visible as physical stamps rather than plain text. */
function FormatSeal({ label, opacity, y }: { label: string; opacity: MotionValue<number>; y: MotionValue<number> }) {
  return (
    <motion.span
      aria-hidden
      style={{ opacity, y, borderColor: 'rgba(138,59,46,0.6)', color: PAPER_STAMP, background: 'rgba(246,241,228,0.65)' }}
      className="grid h-8 w-8 place-items-center rounded-full border-[1.5px] text-[7px] font-bold uppercase tracking-wide sm:h-9 sm:w-9 sm:text-[7.5px]"
    >
      {label}
    </motion.span>
  );
}

function ChapterTools({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);
  const [activeStage, setActiveStage] = useState(0);

  useMotionValueEvent(progress, 'change', (v) => {
    setActiveStage(clamp(Math.floor(v * TOOLS_STAGE_COUNT), 0, TOOLS_STAGE_COUNT - 1));
  });

  // 6 equal segments across the pin's scroll range, each with a small
  // crossfade margin M at its boundaries.
  const M = 0.026;
  const b1 = 1 / 6;
  const b2 = 2 / 6;
  const b3 = 3 / 6;
  const b4 = 4 / 6;
  const b5 = 5 / 6;

  const op1 = useTransform(progress, [0, b1 - M, b1], [1, 1, 0]);
  const op2 = useTransform(progress, [b1 - M, b1, b2 - M, b2], [0, 1, 1, 0]);
  const op3 = useTransform(progress, [b2 - M, b2, b3 - M, b3], [0, 1, 1, 0]);
  const op4 = useTransform(progress, [b3 - M, b3, b4 - M, b4], [0, 1, 1, 0]);
  const op5 = useTransform(progress, [b4 - M, b4, b5 - M, b5], [0, 1, 1, 0]);
  const op6 = useTransform(progress, [b5 - M, b5, 1], [0, 1, 1]);

  const sub1 = useTransform(progress, [0, b1], [0, 1]);
  const sub2 = useTransform(progress, [b1, b2], [0, 1]);
  const sub3 = useTransform(progress, [b2, b3], [0, 1]);
  const sub4 = useTransform(progress, [b3, b4], [0, 1]);
  const sub5 = useTransform(progress, [b4, b5], [0, 1]);
  const sub6 = useTransform(progress, [b5, 1], [0, 1]);

  // stage 1 — ghostwriter
  const beforeOpacity = useTransform(sub1, [0, 0.4, 0.62], [1, 1, 0]);
  const afterOpacity = useTransform(sub1, [0.48, 0.75], [0, 1]);
  const sweepWidth = useTransform(sub1, [0.15, 0.6], ['0%', '100%']);

  // stage 2 — estrutura
  const linePath = useTransform(sub2, [0.12, 0.85], [0, 1]);
  const dot1 = useTransform(sub2, [0.05, 0.2], [0, 1]);
  const dot2 = useTransform(sub2, [0.38, 0.53], [0, 1]);
  const dot3 = useTransform(sub2, [0.72, 0.87], [0, 1]);

  // stage 3 — universo & story bible: 3 mini fichas fanning in, callback to
  // capítulo II's own cast (same names — the world stays one world).
  const uni1Opacity = useTransform(sub3, [0.04, 0.32], [0, 1]);
  const uni1Y = useTransform(sub3, [0.02, 0.36], [30, 0]);
  const uni1Rotate = useTransform(sub3, [0.02, 0.36], [0, -7]);
  const uni2Opacity = useTransform(sub3, [0.2, 0.48], [0, 1]);
  const uni2Y = useTransform(sub3, [0.16, 0.52], [30, 0]);
  const uni2Rotate = useTransform(sub3, [0.16, 0.52], [0, 6]);
  const uni3Opacity = useTransform(sub3, [0.38, 0.66], [0, 1]);
  const uni3Y = useTransform(sub3, [0.34, 0.7], [24, 0]);
  const uni3Rotate = useTransform(sub3, [0.34, 0.7], [0, -3]);

  // stage 4 — colaboração em tempo real: 2 live carets on the same line.
  const collabPresence = useTransform(sub4, [0.02, 0.2], [0, 1]);
  const collabCursor1X = useTransform(sub4, [0.12, 0.96], ['4%', '58%']);
  const collabCursor2X = useTransform(sub4, [0.24, 0.98], ['22%', '84%']);
  const collabLine2Width = useTransform(sub4, [0.1, 0.95], ['0%', '100%']);

  // stage 5 — exportação: selos de formato aparecem, depois o lacre desce.
  const fmt1Opacity = useTransform(sub5, [0.02, 0.18], [0, 1]);
  const fmt1Y = useTransform(sub5, [0, 0.2], [10, 0]);
  const fmt2Opacity = useTransform(sub5, [0.1, 0.26], [0, 1]);
  const fmt2Y = useTransform(sub5, [0.08, 0.28], [10, 0]);
  const fmt3Opacity = useTransform(sub5, [0.18, 0.34], [0, 1]);
  const fmt3Y = useTransform(sub5, [0.16, 0.36], [10, 0]);
  const sealY = useTransform(sub5, [0.3, 0.62], [-70, 0]);
  const sealOpacity = useTransform(sub5, [0.3, 0.46], [0, 1]);
  const sealScale = useTransform(sub5, [0.6, 0.72, 0.84], [1.3, 1, 0.94]);
  const rippleScale = useTransform(sub5, [0.66, 0.98], [0.5, 1.9]);
  const rippleOpacity = useTransform(sub5, [0.66, 0.72, 0.98], [0, 0.55, 0]);

  // stage 6 — capas
  const flipRotate = useTransform(sub6, [0.08, 0.85], [0, 180]);

  const stages = [
    { feature: t('landingStory.chapterTools.stage1Feature', 'ghostwriter ia'), title: t('landingStory.chapterTools.stage1Title', 'A IA reescreve com sua voz.'), benefit: t('landingStory.chapterTools.stage1Benefit', 'nunca mais trave numa frase.') },
    { feature: t('landingStory.chapterTools.stage2Feature', 'estrutura de 3 atos'), title: t('landingStory.chapterTools.stage2Title', 'Três atos, uma linha só.'), benefit: t('landingStory.chapterTools.stage2Benefit', 'seu enredo nunca perde o rumo.') },
    { feature: t('landingStory.chapterTools.stage3Feature', 'universo & story bible'), title: t('landingStory.chapterTools.stage3Title', 'Cada personagem e lugar, sempre à mão.'), benefit: t('landingStory.chapterTools.stage3Benefit', 'fichas vivas que você nunca mais perde de vista.') },
    { feature: t('landingStory.chapterTools.stage4Feature', 'colaboração em tempo real · pro'), title: t('landingStory.chapterTools.stage4Title', 'Duas penas, uma só história.'), benefit: t('landingStory.chapterTools.stage4Benefit', 'convide seu editor ou parceiro por e-mail (como editor, comentarista ou leitor) e escrevam juntos, ao vivo, na mesma página.') },
    { feature: t('landingStory.chapterTools.stage5Feature', 'exportação'), title: t('landingStory.chapterTools.stage5Title', 'Um lacre, e o livro está pronto.'), benefit: t('landingStory.chapterTools.stage5Benefit', 'PDF, DOCX e EPUB em segundos.') },
    { feature: t('landingStory.chapterTools.stage6Feature', 'capas com IA'), title: t('landingStory.chapterTools.stage6Title', 'A capa vira o rosto do livro.'), benefit: t('landingStory.chapterTools.stage6Benefit', 'sua obra com cara de lançamento.') },
  ];

  return (
    <ChapterShell sectionRef={ref} reduced={reduced} heightVh={CHAPTER_VH.tools} ariaLabel={t('landingStory.aria.chapterTools', 'Capítulo — as ferramentas da jornada')} id="ferramentas">
      <div className="mb-6 flex flex-col items-center gap-3 px-2">
        <Eyebrow>{t('landingStory.chapterTools.eyebrow', 'capítulo · as ferramentas da jornada')}</Eyebrow>
        <Headline style={{ fontSize: 'clamp(1.6rem, 4.6vw, 2.6rem)' }}>{t('landingStory.chapterTools.title', 'Você não escreve sozinho — tem um estúdio inteiro ao seu lado.')}</Headline>
        <SubText>{t('landingStory.chapterTools.sub', 'Da primeira frase ao lacre final, cada etapa da jornada tem sua ferramenta.')}</SubText>
      </div>

      <div className="relative h-[220px] w-[min(380px,90vw)] sm:h-[240px]">
        {/* stage 1 — ghostwriter */}
        <motion.div style={{ opacity: op1 }} className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
          <div className="relative w-full rounded-[8px] px-4 py-5" style={{ background: PAPER, boxShadow: '0 20px 50px -14px rgba(0,0,0,0.7), 0 0 0 1px rgba(70,55,30,0.25)' }}>
            <div className="relative min-h-[4.6em]">
              <motion.p style={{ opacity: beforeOpacity }} className="absolute inset-0 font-serif text-[13px] leading-relaxed sm:text-[14.5px]">
                <span style={{ color: PAPER_INK }}>{t('landingStory.chapterTools.stage1Before', 'Ela abriu a porta devagar.')}</span>
              </motion.p>
              <motion.p style={{ opacity: afterOpacity }} className="absolute inset-0 font-serif text-[13px] leading-relaxed sm:text-[14.5px]">
                <span style={{ color: PAPER_INK }}>{t('landingStory.chapterTools.stage1After', 'Ela abriu a porta devagar, o rangido da madeira ecoando pelo corredor vazio.')}</span>
              </motion.p>
            </div>
            <motion.span
              aria-hidden
              style={{ width: sweepWidth, background: `linear-gradient(90deg, ${MOON}, transparent)` }}
              className="absolute bottom-3 left-4 h-[2px] rounded-full"
            />
          </div>
        </motion.div>

        {/* stage 2 — estrutura */}
        <motion.div style={{ opacity: op2 }} className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <svg viewBox="0 0 220 20" className="h-[24px] w-[220px]" aria-hidden>
            <motion.path d="M14,10 L110,10 L206,10" fill="none" stroke="rgba(223,201,147,0.5)" strokeWidth={1.4} style={{ pathLength: linePath }} />
          </svg>
          <div className="flex w-[240px] items-center justify-between">
            {[
              { op: dot1, label: t('landingStory.chapterTools.stage2Act1', 'Ato I') },
              { op: dot2, label: t('landingStory.chapterTools.stage2Act2', 'Ato II') },
              { op: dot3, label: t('landingStory.chapterTools.stage2Act3', 'Ato III') },
            ].map((d, i) => (
              <motion.div key={i} style={{ opacity: d.op, scale: d.op }} className="flex flex-col items-center gap-1.5">
                <span
                  className="grid h-8 w-8 place-items-center rounded-full text-[11px]"
                  style={{ background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 60%, #9A8756)', color: '#141005' }}
                >
                  ✦
                </span>
                <span className="text-[8px] uppercase tracking-[0.18em]" style={{ ...MONO, color: INK_DIM }}>
                  {d.label}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* stage 3 — universo & story bible */}
        <motion.div style={{ opacity: op3 }} className="absolute inset-0 flex items-center justify-center">
          <div className="relative h-[150px] w-[230px] sm:h-[164px] sm:w-[250px]">
            <motion.div
              style={{ opacity: uni1Opacity, y: uni1Y, rotate: uni1Rotate, background: PAPER, boxShadow: '0 18px 40px -14px rgba(0,0,0,0.7), 0 0 0 1px rgba(70,55,30,0.25)' }}
              className="absolute left-0 top-1 w-[108px] rounded-[3px_8px_8px_3px] px-2.5 py-2.5"
            >
              <p className="text-[6.5px] uppercase tracking-[0.2em]" style={{ ...MONO, color: '#A8935F' }}>
                {t('landingStory.chapter2.tagCharacter', 'personagem')}
              </p>
              <p className="mt-1 font-serif text-[11px] font-medium leading-tight" style={{ color: PAPER_INK }}>
                {t('landingStory.chapter2.card1Name', 'Elenya Duskwright')}
              </p>
            </motion.div>
            <motion.div
              style={{ opacity: uni2Opacity, y: uni2Y, rotate: uni2Rotate, background: PAPER, boxShadow: '0 18px 40px -14px rgba(0,0,0,0.7), 0 0 0 1px rgba(70,55,30,0.25)' }}
              className="absolute right-0 top-0 w-[108px] rounded-[3px_8px_8px_3px] px-2.5 py-2.5"
            >
              <p className="text-[6.5px] uppercase tracking-[0.2em]" style={{ ...MONO, color: '#A8935F' }}>
                {t('landingStory.chapter2.tagPlace', 'lugar')}
              </p>
              <p className="mt-1 font-serif text-[11px] font-medium leading-tight" style={{ color: PAPER_INK }}>
                {t('landingStory.chapter2.card2Name', 'Vale de Cinábrio')}
              </p>
            </motion.div>
            <motion.div
              style={{ opacity: uni3Opacity, y: uni3Y, rotate: uni3Rotate, background: PAPER, boxShadow: '0 18px 40px -14px rgba(0,0,0,0.7), 0 0 0 1px rgba(70,55,30,0.25)' }}
              className="absolute bottom-0 left-1/2 w-[118px] -translate-x-1/2 rounded-[3px_8px_8px_3px] px-2.5 py-2.5"
            >
              <p className="text-[6.5px] uppercase tracking-[0.2em]" style={{ ...MONO, color: '#A8935F' }}>
                {t('landingStory.chapter2.tagObject', 'objeto')}
              </p>
              <p className="mt-1 font-serif text-[11px] font-medium leading-tight" style={{ color: PAPER_INK }}>
                {t('landingStory.chapter2.card4Name', 'A Chave de Cinábrio')}
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* stage 4 — colaboração em tempo real */}
        <motion.div style={{ opacity: op4 }} className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
          <div className="relative w-full max-w-[280px] rounded-[10px] px-4 py-4" style={{ background: 'rgba(168,184,232,0.05)', boxShadow: '0 0 0 1px rgba(168,184,232,0.18) inset' }}>
            <motion.span style={{ opacity: collabPresence }} className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full px-2 py-0.5" aria-hidden>
              <span className="ls-bounce h-1.5 w-1.5 rounded-full" style={{ background: '#6EE7A0', animationDuration: '1.1s' }} />
              <span className="text-[7px] uppercase tracking-[0.18em]" style={{ ...MONO, color: INK_DIM }}>
                {t('landingStory.chapterTools.stage4Live', 'ao vivo')}
              </span>
            </motion.span>
            <div className="flex flex-col gap-3 pt-3">
              <span className="block h-[6px] w-[88%] rounded-full" style={{ background: 'rgba(168,184,232,0.16)' }} />
              <span className="relative block h-[6px] w-full rounded-full" style={{ background: 'rgba(168,184,232,0.16)' }}>
                <motion.span aria-hidden style={{ width: collabLine2Width, background: `linear-gradient(90deg, ${PERIWINKLE}, ${MOON_HOT})` }} className="absolute inset-y-0 left-0 rounded-full opacity-70" />
                <motion.span aria-hidden style={{ left: collabCursor1X, opacity: collabPresence }} className="absolute -top-[22px] flex -translate-x-1/2 flex-col items-center gap-0.5">
                  <span className="rounded-full px-1.5 py-0.5 text-[6px] font-semibold uppercase tracking-wide" style={{ ...MONO, background: PERIWINKLE, color: '#0A101F' }}>
                    {t('landingStory.chapterTools.stage4You', 'você')}
                  </span>
                  <span className="h-3 w-[2px] animate-pulse" style={{ background: PERIWINKLE }} />
                </motion.span>
                <motion.span aria-hidden style={{ left: collabCursor2X, opacity: collabPresence }} className="absolute -top-[22px] flex -translate-x-1/2 flex-col items-center gap-0.5">
                  <span className="rounded-full px-1.5 py-0.5 text-[6px] font-semibold uppercase tracking-wide" style={{ ...MONO, background: MOON_HOT, color: '#0A101F' }}>
                    {t('landingStory.chapterTools.stage4Ally', 'ana')}
                  </span>
                  <span className="h-3 w-[2px] animate-pulse" style={{ background: MOON_HOT }} />
                </motion.span>
              </span>
              <span className="block h-[6px] w-[68%] rounded-full" style={{ background: 'rgba(168,184,232,0.16)' }} />
            </div>
          </div>
        </motion.div>

        {/* stage 5 — exportação */}
        <motion.div style={{ opacity: op5 }} className="absolute inset-0 flex flex-col items-center justify-center gap-5">
          <div className="relative w-[200px] rounded-[3px_8px_8px_3px] px-5 py-6 text-center" style={{ background: PAPER, boxShadow: '0 20px 50px -14px rgba(0,0,0,0.7), 0 0 0 1px rgba(70,55,30,0.25)' }}>
            <p className="font-mono text-[7px] uppercase tracking-[0.22em]" style={{ color: '#A8935F' }}>
              {t('landingStory.chapterTools.stage5Stamp', 'selado')}
            </p>
            <div className="mt-2.5 flex items-center justify-center gap-2.5">
              <FormatSeal label="PDF" opacity={fmt1Opacity} y={fmt1Y} />
              <FormatSeal label="DOCX" opacity={fmt2Opacity} y={fmt2Y} />
              <FormatSeal label="EPUB" opacity={fmt3Opacity} y={fmt3Y} />
            </div>
            <motion.span
              aria-hidden
              style={{ scale: rippleScale, opacity: rippleOpacity, background: 'radial-gradient(circle, rgba(223,201,147,0.5), transparent 70%)' }}
              className="pointer-events-none absolute -bottom-4 left-1/2 h-[46px] w-[46px] -translate-x-1/2 rounded-full"
            />
            <motion.span
              aria-hidden
              style={{ y: sealY, opacity: sealOpacity, scale: sealScale, background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 55%, #9A8756)', color: '#141005', boxShadow: '0 8px 22px rgba(0,0,0,0.5), 0 0 24px rgba(223,201,147,0.45)' }}
              className="absolute -bottom-5 left-1/2 grid h-[46px] w-[46px] -translate-x-1/2 place-items-center rounded-full text-[16px]"
            >
              ✦
            </motion.span>
          </div>
        </motion.div>

        {/* stage 6 — capas */}
        <motion.div style={{ opacity: op6 }} className="absolute inset-0 flex items-center justify-center" >
          <div className="relative h-[164px] w-[112px]" style={{ perspective: 800 }}>
            <motion.div style={{ rotateY: flipRotate, transformStyle: 'preserve-3d' }} className="relative h-full w-full">
              <span className="mcover absolute inset-0 flex items-end p-2.5" style={{ backfaceVisibility: 'hidden' }}>
                <span className="mo" />
                <span className="font-serif text-[11px] font-medium leading-tight text-[#F2E9D4]">{t('landingStory.chapter2.card2Name', 'Vale de Cinábrio')}</span>
              </span>
              <span
                className="mcover absolute inset-0 flex items-end p-2.5"
                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)', background: 'linear-gradient(168deg,#3A2A52,#1B1230 55%,#0A1024)' }}
              >
                <span className="mo" style={{ background: 'radial-gradient(circle at 34% 30%, var(--ember-hot), var(--ember) 60%, #9A8756)' }} />
                <span className="font-serif text-[11px] font-medium leading-tight text-[#F2E9D4]">{t('landingStory.chapter2.card1Name', 'Elenya Duskwright')}</span>
              </span>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* dots de progresso */}
      <div className="mt-5 flex items-center gap-2" aria-hidden>
        {Array.from({ length: TOOLS_STAGE_COUNT }, (_, i) => (
          <span
            key={i}
            className="h-1.5 rounded-full transition-all duration-300"
            style={{ width: activeStage === i ? 18 : 6, background: activeStage === i ? MOON_HOT : 'rgba(168,184,232,0.28)' }}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeStage}
          initial={reduced ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduced ? undefined : { opacity: 0, y: -8 }}
          transition={{ duration: 0.35 }}
          className="mt-4 flex flex-col items-center gap-2 px-4 text-center"
        >
          <p className="text-[9px] uppercase tracking-[0.26em]" style={{ ...MONO, color: MOON_TEXT }}>
            {stages[activeStage].feature}
          </p>
          <p className="max-w-[26ch] font-serif text-[17px] font-medium leading-snug sm:text-[19px]" style={{ color: INK }}>
            {stages[activeStage].title}
          </p>
          <p className="text-[12px]" style={{ color: INK_DIM }}>
            {stages[activeStage].benefit}
          </p>
        </motion.div>
      </AnimatePresence>
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO IV · ALIADOS
// ═══════════════════════════════════════════════════════════════════════════
function ChapterFour({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);

  const featherAX = useTransform(progress, [0, 0.5], [-90, -14]);
  const featherAY = useTransform(progress, [0, 0.5], [-70, -8]);
  const featherARotate = useTransform(progress, [0, 0.5], [-60, -32]);
  const featherAOpacity = useTransform(progress, [0, 0.15], [0, 1]);

  const featherBX = useTransform(progress, [0, 0.5], [90, 14]);
  const featherBY = useTransform(progress, [0, 0.5], [-70, 8]);
  const featherBRotate = useTransform(progress, [0, 0.5], [60, 32]);
  const featherBOpacity = useTransform(progress, [0, 0.15], [0, 1]);

  const allies = [
    t('landingStory.chapter4.allyEditing', 'edição'),
    t('landingStory.chapter4.allyDesign', 'design de capa'),
    t('landingStory.chapter4.allyMarketing', 'marketing'),
  ];

  return (
    <ChapterShell sectionRef={ref} reduced={reduced} heightVh={CHAPTER_VH.four} ariaLabel={t('landingStory.aria.chapter4', 'Capítulo quatro — aliados')}>
      <div className="mb-6">
        <Eyebrow>{t('landingStory.chapter4.eyebrow', 'capítulo IV · aliados')}</Eyebrow>
      </div>

      <div className="relative flex h-[110px] w-[180px] items-center justify-center">
        <motion.span aria-hidden style={{ x: featherAX, y: featherAY, rotate: featherARotate, opacity: featherAOpacity }} className="absolute text-[46px]">
          🪶
        </motion.span>
        <motion.span aria-hidden style={{ x: featherBX, y: featherBY, rotate: featherBRotate, opacity: featherBOpacity }} className="absolute text-[46px]">
          🪶
        </motion.span>
      </div>

      <motion.div variants={staggerContainer(0.13, 0.4)} {...revealProps(reduced)} className="mt-8 flex items-center gap-4 sm:gap-6">
        {allies.map((label) => (
          <motion.div key={label} variants={popIn} className="flex flex-col items-center gap-2">
            <span
              className="grid h-11 w-11 place-items-center rounded-full text-[13px] sm:h-12 sm:w-12"
              style={{ background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 60%, #9A8756)', color: '#141005', boxShadow: '0 6px 18px rgba(0,0,0,0.4)' }}
            >
              ✦
            </span>
            <span className="text-[9px] uppercase tracking-[0.18em]" style={{ ...MONO, color: INK_DIM }}>
              {label}
            </span>
          </motion.div>
        ))}
      </motion.div>

      <div className="mt-8 flex flex-col items-center gap-3 px-2">
        <Headline style={{ fontSize: 'clamp(1.6rem, 4.6vw, 2.7rem)' }}>{t('landingStory.chapter4.title', 'Ninguém termina um livro sozinho.')}</Headline>
        <SubText>{t('landingStory.chapter4.sub', 'Editores, designers de capa e especialistas em marketing — um mercado inteiro de aliados, a um lacre de distância.')}</SubText>
      </div>

      <MicroPitch
        feature={t('landingStory.chapter4.pitchFeature', 'marketplace de aliados')}
        benefit={t('landingStory.chapter4.pitchBenefit', 'editores, revisores e capistas — contratados sem sair do livro.')}
      />
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO · ESCOLHA SUA JORNADA (planos — ainda noite, antes do amanhecer)
// ═══════════════════════════════════════════════════════════════════════════
function PlanCard({ name, price, desc, badge, perMonth }: { name: string; price: string; desc: string; badge?: string; perMonth: string }) {
  return (
    <motion.div variants={fadeUp} className="dcard relative flex flex-col gap-2 p-5">
      {badge && (
        <span className="absolute right-4 top-4 rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em]" style={{ ...MONO, background: MOON_HOT, color: '#141005' }}>
          {badge}
        </span>
      )}
      <p className="font-serif text-[17px] font-semibold" style={{ color: INK }}>
        {name}
      </p>
      <p className="flex items-baseline gap-1">
        <span className="font-serif text-[24px] font-bold" style={{ color: MOON_TEXT }}>
          {price}
        </span>
        <span className="text-[10px]" style={{ ...MONO, color: INK_DIM }}>
          {perMonth}
        </span>
      </p>
      <p className="text-[12px] leading-relaxed" style={{ color: INK_DIM }}>
        {desc}
      </p>
    </motion.div>
  );
}

function ChapterPricing({ reduced }: { reduced: boolean }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const { fmt, monthly } = useCurrency();

  return (
    <ChapterShell sectionRef={ref} reduced={reduced} heightVh={CHAPTER_VH.pricing} ariaLabel={t('landingStory.aria.chapterPricing', 'Capítulo — escolha sua jornada')} id="planos">
      <div className="mb-6 flex flex-col items-center gap-3 px-2">
        <Eyebrow>{t('landingStory.chapterPricing.eyebrow', 'capítulo · escolha sua jornada')}</Eyebrow>
        <Headline style={{ fontSize: 'clamp(1.7rem, 5vw, 2.9rem)' }}>{t('landingStory.chapterPricing.title', 'Toda jornada tem seu próprio ritmo.')}</Headline>
        <SubText>{t('landingStory.chapterPricing.sub', '7 dias de tudo, grátis. Depois siga no plano Grátis ou assine — sem cartão pra começar.')}</SubText>
      </div>

      <motion.div variants={staggerContainer(0.1)} {...revealProps(reduced)} className="relative mx-auto w-full max-w-4xl">
        <motion.p variants={fadeUp} className="mb-5 text-center text-[10px] uppercase tracking-[0.28em]" style={{ ...MONO, color: INK_DIM }}>
          {t('landingStory.chapterPricing.plansTitle', 'Preços, sem mistério')}
        </motion.p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <PlanCard
            name={t('landingStory.chapterPricing.freeName', 'Grátis')}
            price={fmt(0)}
            desc={t('landingStory.chapterPricing.freeDesc', 'Editor completo, export PDF/DOCX e 30 ações de IA por mês. Escrita solo — colaboração é Pro.')}
            perMonth={t('landingStory.chapterPricing.perMonth', '/mês')}
          />
          <PlanCard
            name={t('landingStory.chapterPricing.iaStudioName', 'IA Studio')}
            price={fmt(monthly('ia-studio'))}
            desc={t('landingStory.chapterPricing.iaStudioDesc', 'IA de escrita (~1.500 ações/mês) e 10 capas por mês.')}
            perMonth={t('landingStory.chapterPricing.perMonth', '/mês')}
          />
          <PlanCard
            name={t('landingStory.chapterPricing.bundleName', 'Bundle "Tudo"')}
            price={fmt(monthly('bundle'))}
            desc={t('landingStory.chapterPricing.bundleDesc', 'Todos os add-ons juntos — o melhor valor.')}
            badge={t('landingStory.chapterPricing.bundleBadge', 'melhor valor')}
            perMonth={t('landingStory.chapterPricing.perMonth', '/mês')}
          />
        </div>
        <motion.div variants={fadeUp} className="mt-6 text-center">
          <a href="/#pricing" className="text-[11px] uppercase tracking-[0.18em] underline-offset-4 hover:underline" style={{ ...MONO, color: PERIWINKLE }}>
            {t('landingStory.chapterPricing.seeAll', 'ver planos completos')} →
          </a>
        </motion.div>
      </motion.div>
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CAPÍTULO FINAL · O AMANHECER — cinema vivo (gaivotas + nuvens) + CTA +
// mini-footer, tudo dentro do céu dourado. Última cena da página.
// ═══════════════════════════════════════════════════════════════════════════
function Gull({ lane, duration, delay, size }: { lane: number; duration: number; delay: number; size: number }) {
  return (
    <motion.span
      aria-hidden
      className="pointer-events-none absolute"
      style={{ top: `${lane}%`, left: 0 }}
      animate={{ x: ['-15vw', '115vw'] }}
      transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
    >
      <motion.svg
        width={size}
        height={size * 0.42}
        viewBox="0 0 24 10"
        animate={{ scaleY: [1, 0.42, 1] }}
        transition={{ duration: 0.7, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut', delay: delay * 0.3 }}
        style={{ transformOrigin: '50% 50%' }}
      >
        <path d="M0,8 Q6,0 12,7 Q18,0 24,8" stroke="rgba(10,16,31,0.5)" strokeWidth={1.6} fill="none" strokeLinecap="round" />
      </motion.svg>
    </motion.span>
  );
}

/**
 * A single drifting cloud blob. Trajectory always starts/ends well off-screen
 * (-42vw → 142vw) so the loop reset is invisible ("seamless" per the brief) —
 * Framer Motion's default `repeatType: 'loop'` just snaps back to the start
 * keyframe, and since both ends are outside the viewport, no pop is visible.
 * `angle` adds a small vertical drift over the crossing so each parallax
 * layer reads as moving in a very slightly different direction, not just a
 * different speed — the "3 camadas com direções levemente diferentes" ask.
 */
function CloudBlob({ top, size, blur, duration, delay, tint, angle }: { top: number; size: number; blur: number; duration: number; delay: number; tint: string; angle: number }) {
  return (
    <motion.span
      aria-hidden
      className="pointer-events-none absolute rounded-full"
      style={{ top: `${top}%`, left: 0, width: size, height: size * 0.38, background: tint, filter: `blur(${blur}px)` }}
      animate={{ x: ['-42vw', '142vw'], y: ['0vh', `${angle}vh`, '0vh'] }}
      transition={{ duration, delay, repeat: Infinity, ease: 'linear' }}
    />
  );
}

/** 3 parallax depth layers — far (small, subtle), mid, and near (large,
 * fastest, warmest) — for a genuinely cinematic, VISIBLY moving dawn sky.
 * Crossings are fast enough (26–45s full pass) to read as motion within a
 * few seconds of looking, alphas are high-contrast (0.25–0.45) with blur,
 * and each layer spawns `count` clouds spread across the whole crossing via
 * evenly-staggered negative delays — so several are always on screen, never
 * an empty sky while the loop resets off-viewport. */
const DAWN_CLOUD_LAYERS = [
  { key: 'far', top: 7, size: 130, blur: 30, duration: 42, tint: 'rgba(255,240,222,0.26)', angle: -2, count: 3 },
  { key: 'mid', top: 23, size: 190, blur: 42, duration: 33, tint: 'rgba(255,228,200,0.35)', angle: 3, count: 3 },
  { key: 'near', top: 41, size: 258, blur: 54, duration: 26, tint: 'rgba(255,214,176,0.43)', angle: -4, count: 3 },
] as const;

const DAWN_CLOUDS = DAWN_CLOUD_LAYERS.flatMap((layer) =>
  Array.from({ length: layer.count }, (_, k) => ({
    key: `${layer.key}-${k}`,
    top: layer.top + ((k * 7) % 9),
    size: layer.size + ((k * 13) % 24),
    blur: layer.blur,
    duration: layer.duration + ((k * 5) % 6),
    // Evenly spread negative delays across the layer's own duration so the
    // clouds start "mid-flight" — several always visible, none pop in/out.
    delay: -((k / layer.count) * layer.duration),
    tint: layer.tint,
    angle: layer.angle,
  })),
);

function ChapterFive({ reduced, theme }: { reduced: boolean; theme: 'light' | 'dark' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const progress = useChapterProgress(ref, reduced);

  const sunY = useTransform(progress, [0.08, 0.6], [200, 40]);
  const sunOpacity = useTransform(progress, [0.08, 0.35], [0, 1]);
  const textOpacity = useTransform(progress, [0.4, 0.62], [0, 1]);
  const textY = useTransform(progress, [0.4, 0.65], [18, 0]);
  const ctaOpacity = useTransform(progress, [0.62, 0.8], [0, 1]);
  const ctaY = useTransform(progress, [0.62, 0.82], [16, 0]);
  const cinemaOpacity = useTransform(progress, [0.16, 0.4], [0, 1]);

  // The dark finale sits on a saturated dusk (deep coral/amber), where white
  // text pops. The light finale's "amanhecer" is a much paler gold — white
  // would nearly vanish there, so this whole scene swaps to a dark ink
  // instead, matching the owner's "AA+ contrast" requirement.
  const isLight = theme === 'light';
  const dawnHeadline = isLight ? '#1A1F35' : '#FFFFFF';
  const dawnBody = isLight ? 'rgba(26,31,53,0.82)' : 'rgba(255,255,255,0.86)';
  const dawnBodyDim = isLight ? 'rgba(26,31,53,0.72)' : 'rgba(255,255,255,0.78)';
  const dawnFaint = isLight ? 'rgba(26,31,53,0.62)' : 'rgba(255,255,255,0.65)';
  const dawnBorder = isLight ? 'rgba(26,31,53,0.16)' : 'rgba(255,255,255,0.16)';

  return (
    <ChapterShell
      sectionRef={ref}
      reduced={reduced}
      heightVh={CHAPTER_VH.five}
      ariaLabel={t('landingStory.aria.chapter5', 'Capítulo final — o amanhecer')}
      background={
        <>
          {/* No local opaque background here — GlobalSky already sweeps all the
              way through the dawn palette by the time the document scroll
              reaches this chapter's range, so the handoff is seamless (single
              color authority for the whole page, zero seam). Only the sun disc
              and the cinema layer (clouds + gulls) are local decoration. */}
          <motion.div aria-hidden style={{ y: sunY, opacity: sunOpacity }} className="pointer-events-none absolute bottom-[-90px] left-1/2 h-[240px] w-[240px] -translate-x-1/2 rounded-full">
            <div className="h-full w-full rounded-full" style={{ background: 'radial-gradient(circle,#FFE1AE,#F6E9C8 55%,rgba(223,201,147,0) 75%)', filter: 'blur(6px)' }} />
          </motion.div>

          {/* cinema vivo — nuvens em 3 camadas de parallax (deriva lenta, loop
              seamless off-screen) + gaivotas cruzando o céu dourado */}
          {!reduced && (
            <motion.div aria-hidden style={{ opacity: cinemaOpacity }} className="pointer-events-none absolute inset-0 overflow-hidden">
              {DAWN_CLOUDS.map((c) => (
                <CloudBlob key={c.key} top={c.top} size={c.size} blur={c.blur} duration={c.duration} delay={c.delay} tint={c.tint} angle={c.angle} />
              ))}
              <Gull lane={24} duration={16} delay={0} size={22} />
              <Gull lane={30} duration={19} delay={-6} size={17} />
              <Gull lane={20} duration={22} delay={-13} size={19} />
            </motion.div>
          )}
        </>
      }
    >
      <motion.div style={{ opacity: textOpacity, y: textY }} className="relative flex flex-col items-center gap-3 px-2">
        <Eyebrow style={{ color: isLight ? MOON_TEXT : MOON_HOT }}>{t('landingStory.chapter5.eyebrow', 'capítulo final · o amanhecer')}</Eyebrow>
        <Headline style={{ color: dawnHeadline }}>{t('landingStory.chapter5.title', 'Manuscrito completo.')}</Headline>
        <p className="font-serif italic" style={{ fontSize: 'clamp(1rem, 2.4vw, 1.3rem)', color: dawnBody }}>
          {t('landingStory.chapter5.sub', 'Termine seu livro.')}
        </p>
      </motion.div>

      <motion.div style={{ opacity: ctaOpacity, y: ctaY }} className="relative mt-8 flex flex-col items-center gap-5 px-4 text-center">
        <p className="max-w-[26ch] font-serif text-[clamp(1.3rem,3.2vw,1.9rem)] font-medium leading-tight" style={{ color: dawnHeadline }}>
          {t('landingStory.chapter5.ctaTitle', 'A próxima história em branco é a sua.')}
        </p>
        <p className="text-[13px]" style={{ color: dawnBodyDim }}>
          {t('landingStory.chapter5.ctaSub', 'Comece a escrever hoje — grátis, para sempre.')}
        </p>
        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <button type="button" onClick={() => navigate('/app?auth=signup')} className="go-ember">
            {t('landingStory.chapter5.ctaSignup', 'Criar conta grátis')}
          </button>
          <a href={APK_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost-mock inline-flex items-center gap-2">
            <Download size={13} aria-hidden />
            {t('landingStory.chapter5.ctaDownload', 'Baixar app Android')}
          </a>
        </div>

        {/* mini-footer discreto, dentro do céu dourado */}
        <div className="mt-10 flex flex-col items-center gap-2.5 border-t pt-6" style={{ borderColor: dawnBorder }}>
          <a href="/" className="flex items-center gap-2" aria-label="reformu.lat">
            <img src="/log-black.png" alt="" aria-hidden="true" className="h-4 w-4 object-contain dark:hidden" />
            <img src="/log-white.png" alt="" aria-hidden="true" className="hidden h-4 w-4 object-contain dark:block" />
            <span className="font-serif text-[12px] font-semibold" style={{ color: dawnHeadline }}>
              reformu<span style={{ color: '#3A2A1C' }}>.</span>lat
            </span>
          </a>
          <p className="text-[9px]" style={{ ...MONO, color: dawnFaint }}>
            © {new Date().getFullYear()} reformu.lat · {t('landingStory.chapter5.footerTagline', 'Feito sob a luz da lua')}
          </p>
          <div className="flex gap-4">
            <a href="/termos" className="text-[9px] uppercase tracking-[0.14em] hover:underline" style={{ ...MONO, color: dawnFaint }}>
              {t('landingStory.chapter5.footerTerms', 'Termos de Uso')}
            </a>
            <a href="/privacidade" className="text-[9px] uppercase tracking-[0.14em] hover:underline" style={{ ...MONO, color: dawnFaint }}>
              {t('landingStory.chapter5.footerPrivacy', 'Política de Privacidade')}
            </a>
          </div>
        </div>
      </motion.div>
    </ChapterShell>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Root
// ═══════════════════════════════════════════════════════════════════════════
export function LandingStoryPage() {
  const reducedRaw = useReducedMotion();
  const reduced = !!reducedRaw;

  // The story used to force the "Odisseia" dark scope regardless of the
  // app's own sun/moon toggle. It now reads the REAL resolved theme so the
  // header's toggle actually does something — `sepia` (an in-app-only
  // theme, unreachable from this page's own ThemeSwitch) falls back to the
  // light sky, which is the closer visual match of the two.
  const { resolvedTheme } = useTheme();
  const theme: 'light' | 'dark' = resolvedTheme === 'dark' ? 'dark' : 'light';

  return (
    // `.ls-root` + `dark`/`light` scopes both the Odisseia CSS custom
    // properties (--card, --border, --primary, ...) AND this file's own
    // --ls-ink/--ls-ink-dim/--ls-moon-text/--ls-shadow/--ls-nav-rgb tokens
    // (see THEME_VARS_CSS) to the resolved theme, so the shared
    // .dcard/.go-ember/.btn-ghost-mock/.candle-mini classes below — and every
    // component in this file that reads INK/INK_DIM/MOON_TEXT — automatically
    // follow the reader's own toggle.
    <div className={`ls-root ${theme} relative w-full`} style={{ background: theme === 'dark' ? '#0A101F' : '#EDE7F5' }}>
      <StoryStyles />
      <GlobalSky reduced={reduced} theme={theme} />
      <StoryProgressBar />
      <StoryNav reduced={reduced} />
      <ChapterZero reduced={reduced} />
      <ChapterOne reduced={reduced} />
      <ChapterTwo reduced={reduced} />
      <ChapterThree reduced={reduced} />
      <ChapterTools reduced={reduced} />
      <ChapterFour reduced={reduced} />
      <ChapterPricing reduced={reduced} />
      <ChapterFive reduced={reduced} theme={theme} />
    </div>
  );
}

export default LandingStoryPage;
